// MeetingsPage — AI 노트. 녹음/업로드→STT 전사→Claude 요약, 폴더·공유·휴지통.
import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconArrowsMove,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconFileText,
  IconFolder,
  IconFolderOpen,
  IconFolderPlus,
  IconHome,
  IconMicrophone,
  IconPencil,
  IconPlus,
  IconShare,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { meetingAPI } from '../api/client';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import { PageHeader } from '../components/ui';
import { useRecording } from '../contexts/RecordingContext';
import { canSeeMenu } from '../utils/menuAccess';
import useIsMobile from '../hooks/useIsMobile';
import { scaleOf } from '../lib/floatingBounds';
import { folderMenuPos } from './meetings/folderMenuPos';
import { alertDialog, confirmDialog, promptDialog } from '../components/AppDialog';
import { CANCELLABLE_STT_STATUSES, MEETING_THEME, STATUS, audioDownloadName, blobDownload, cleanSearchText, fmtDur, meetingFileBase, speakerDisplayMap, diarInfo, smBtn, softWrap } from '../utils/meetingUtil';
import MeetingList from './meetings/MeetingList';
import MeetingDetail from './meetings/MeetingDetail';
import MeetingHome from './meetings/MeetingHome';
import { buildSummaryMarkdown, buildSummaryNotesMarkdown, buildSummaryNotesText, buildSummaryText } from './meetings/summaryNotesText';
import Modal from './meetings/Modal';
import NewNoteView from './meetings/NewNoteView';
import RecordingView from './meetings/RecordingView';
import FolderTreePicker from './meetings/FolderTreePicker';
import MobileFolderDrawer from './meetings/MobileFolderDrawer';
import { flattenTree, folderCaps, folderDeleteMessage, isInSubtree, readExpanded, shareChangeText, writeExpanded } from '../utils/meetingFolderTree';

// 상태 상수·포맷 헬퍼는 utils/meetingUtil 로 분리(상단 import).
STATUS.diarizing = { label: '\uD654\uC790 \uBD84\uC11D \uC911', color: '#356c9b', bg: '#d3e7f9' };
STATUS.rediarize = { label: '\uD654\uC790 \uBD84\uC11D \uB300\uAE30', color: '#356c9b', bg: '#d3e7f9' };

const T = MEETING_THEME;
// 🔴 cancelling(중지 요청 뒤 워커 감지 대기)도 바쁨이다 — 빼면 다른 바쁜 노트가 없을 때 4초 새로고침이 끊겨
//    화면이 «중지하는 중…»에 멈춘다(C3c). 상수 CANCELLABLE_STT_STATUSES 는 ⋮ 메뉴 조건이라 바꾸지 않는다.
const isSttBusy = (m) => !!m && (CANCELLABLE_STT_STATUSES.includes(m.status) || m.status === 'cancelling');
const hasFolderSummaryPrompt = (folders, folderId) => {
  if (folderId === null || folderId === undefined || folderId === '') return false;
  const folder = (Array.isArray(folders) ? folders : []).find((f) => String(f.id) === String(folderId));
  return !!String(folder?.summary_prompt || '').trim();
};
const summaryModeDefaultForFolder = (folders, folderId) => (hasFolderSummaryPrompt(folders, folderId) ? 'folder' : 'default');
// 노트 검색어 정리(cleanSearchText)는 utils/meetingUtil 로 옮겼다 — 홈 검색(MeetingHome)과 함께 쓴다.
// 첫 화면 = «홈»(클로바 묶음 F2b). URL 에 ?view=all 이 있으면 예전처럼 「전체 노트」 목록으로 시작한다.
const initialHome = () => {
  if (typeof window === 'undefined') return true;
  return new URLSearchParams(window.location.search).get('view') !== 'all';
};
// 데스크톱 폴더 패널 폭(경계선 드래그 · 더블클릭 = 기본 폭). 저장값 = 사용자 선호이고, 화면에는 행 폭에 맞게
// 줄여서만 그린다(MeetingDetail 의 notesWShown 과 같은 방식 — 창을 좁혔다 넓히면 정한 폭이 돌아온다).
const FOLDER_PANEL_KEY = 'gwMeetingFolderPanelW';
const FOLDER_PANEL_DEFAULT = 230;   // 지금 너비 = 더블클릭 복귀값
const FOLDER_PANEL_MIN = 200;
const FOLDER_PANEL_MAX = 480;
const NOTE_AREA_MIN = 420;          // 오른쪽(목록·홈·상세) 최소 폭 — MeetingDetail TRANSCRIPT_MIN 과 같은 값
// 🔴 최소값이 최대값보다 우선한다 — 행이 620 미만이면 상한이 200 으로 고정되고 오른쪽이 420 아래로 내려가는 것을 허용한다.
//    행 폭 0(모름 · ResizeObserver 없음 · jsdom)이면 480 상한만 건다.
const folderPanelMaxFor = (rowW) => (rowW > 0 ? Math.max(FOLDER_PANEL_MIN, Math.min(FOLDER_PANEL_MAX, rowW - NOTE_AREA_MIN)) : FOLDER_PANEL_MAX);
const clampFolderPanelW = (px, rowW) => Math.round(Math.min(Math.max(px, FOLDER_PANEL_MIN), folderPanelMaxFor(rowW)));
const readFolderPanelW = () => {
  try {
    const raw = window.localStorage.getItem(FOLDER_PANEL_KEY);
    // 🔴 null·빈값을 먼저 거른다 — Number(null) === 0 이라 그대로 두면 기본값 대신 200 으로 잘린다.
    if (raw == null || String(raw).trim() === '') return FOLDER_PANEL_DEFAULT;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return FOLDER_PANEL_DEFAULT;
    return clampFolderPanelW(n, 0);
  } catch (e) {
    return FOLDER_PANEL_DEFAULT;   // 저장소 접근 불가
  }
};

export default function MeetingsPage() {
  const navigate = useNavigate();
  // 주소 인자(?open= · ?folder=)는 라우터에서 읽어 효과의 의존성으로 둔다 — 이미 이 화면에 있을 때
  // 알림·통합검색이 navigate 로 오면 같은 Route 라 다시 마운트되지 않아, 마운트 때 한 번만 읽으면 무반응이다.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  const folderParam = searchParams.get('folder');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [folders, setFolders] = useState([]);
  const [foldersLoaded, setFoldersLoaded] = useState(false);   // 폴더 목록을 한 번이라도 받았나(?folder= 판정 시점)
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState('');
  const [sel, setSel] = useState(null);            // 선택 폴더(null=미분류)
  // 🔴 allView·sort·order·homeView 는 useState 초기값으로만 정한다 — 마운트 뒤 effect 로 바꾸면
  //    loadMeetings 가 새로 만들어지고 그 effect 가 상세를 닫아 ?open= 딥링크가 풀린다.
  const [allView, setAllView] = useState(true);    // 「전체 노트」(scope=all) — 기본 선택
  // «홈»(최근 노트·달력) — 첫 화면. allView 는 홈에서도 true 로 둔다(부모 목록 = 전체 노트 범위).
  // 🔴 보기를 바꾸는 자리(폴더·전체 노트·폴더 만들기·휴지통)에서 false 로 끈다. «새 노트»는 끄지 않는다(닫으면 홈).
  const [homeView, setHomeView] = useState(initialHome);
  const [sort, setSort] = useState('updated');     // 서버 정렬 기준
  const [order, setOrder] = useState('desc');      // 서버 정렬 방향
  const [stats, setStats] = useState(null);        // 왼쪽 메뉴 개수(all·unfiled·folders)
  const [newFolderOpen, setNewFolderOpen] = useState(false);   // 폴더 추가 입력칸
  const [newFolderName, setNewFolderName] = useState('');
  // 폴더 트리(최대 3단계) — 펼친 폴더(localStorage 유지) · 하위 폴더 입력칸 · 폴더 위치 옮기기 모달
  const [expandedKeys, setExpandedKeys] = useState(() => readExpanded());
  const [childFolderParent, setChildFolderParent] = useState(null);   // 하위 폴더를 만들 부모 id
  const [childFolderName, setChildFolderName] = useState('');
  const [folderMove, setFolderMove] = useState(null);                 // 위치를 옮길 폴더
  // 폴더 행 «⋯» 메뉴 — 열린 행 하나만 {key(행 itemKey), top, right(고정 위치 px)} · null = 닫힘
  const [folderMenu, setFolderMenu] = useState(null);
  const folderMenuBtnRef = useRef(null);     // 열린 행의 «⋯» 단추
  const folderMenuPanelRef = useRef(null);   // 열린 메뉴 패널
  const [folderTrash, setFolderTrash] = useState([]);                 // 삭제된 폴더(휴지통 보기)
  const [newNoteOpen, setNewNoteOpen] = useState(false);       // «새 노트» 화면(제목·폴더·참석자·언어·화자분리)
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [meetingsError, setMeetingsError] = useState('');
  // 노트 목록 검색(서버 q) — listQuery = 입력값 · listQueryRef = 요청에 싣는 값(300ms 디바운스 뒤 정리한 값)
  // · meetingsQuery = 지금 목록을 받아 온 요청의 검색어(빈 문구 «검색 결과가 없습니다.» 기준).
  // 🔴 loadMeetings 는 검색어를 **ref 로 읽는다** — useCallback 의존성에 넣으면 loadMeetings 가 새로 만들어지고
  //    아래 `loadMeetings(); closeMeetingDetail();` effect 가 검색 중 연 상세를 닫는다(위 allView 주석과 같은 함정).
  const [listQuery, setListQuery] = useState('');
  const listQueryRef = useRef('');
  const [meetingsQuery, setMeetingsQuery] = useState('');
  // 홈 검색어(H2) — 홈은 보일 때만 마운트되므로 여기서 들고 있어야 «검색 → 결과 열기 → 닫기» 뒤에도 남는다.
  // ref 라서 입력마다 이 화면이 다시 그려지지 않는다. 목록 검색어(listQuery)와는 따로다.
  const homeQueryRef = useRef('');
  const [active, setActive] = useState(null);      // 선택 회의록 상세
  const [sttStarting, setSttStarting] = useState(false);
  const [fpEdit, setFpEdit] = useState(false);     // 폴더 요약양식 편집
  const [fpText, setFpText] = useState('');
  const {
    recording,
    recFinalizing,
    uploadVersion,
    recordingScreenOpen,
    recordingScreenMinimized,
    minimizeRecordingScreen,
    recordingBusy,
    pendingUploads = [],
    retryPendingUpload,
    discardPendingUpload,
    startRec: startRecording,
  } = useRecording();
  const fileUploadSeqRef = useRef(0);
  const meetingsSeqRef = useRef(0);        // 늦게 온 옛 목록 응답이 새 목록을 덮지 않게(fileUploadSeqRef 와 같은 방식)
  const statsSeqRef = useRef(0);
  const folderCreateRef = useRef(false);   // 폴더 생성 잠금 — Enter 직후 입력칸이 사라지며 생기는 blur 가 한 번 더 만들지 못하게
  const childCreateRef = useRef(false);    // 하위 폴더 생성 잠금(위와 같은 이유)
  // 검색어가 있을 때 하위 폴더까지 받을지 고르려고 폴더 목록을 ref 로도 든다 — loadMeetings 의존성에 넣으면
  // 폴더 목록을 다시 읽을 때마다 loadMeetings 가 새로 만들어지고 아래 effect 가 상세를 닫는다(listQueryRef 와 같은 함정).
  const foldersRef = useRef([]);
  const openParamKeyRef = useRef(null);   // ?open= 을 판정한 이동(location.key)
  const folderReqRef = useRef(0);         // ?folder= 판정 번호 — 새 ?folder=·?open= 이 오면 늘려 늦게 온 옛 판정을 버린다
  const foldersPromiseRef = useRef(null); // 마지막 폴더 목록 읽기 — ?folder= 첫 진입은 마운트 때 시작한 읽기를 기다린다
  const activeRef = useRef(active);
  const [fileUploadPct, setFileUploadPct] = useState(null);   // 업로드 진행률(null=유휴)
  const [diarizeUpload, setDiarizeUpload] = useState(false);
  const [rediarizing, setRediarizing] = useState(false);
  const [longAsk, setLongAsk] = useState(false);       // 긴 전사 요약범위 선택 모달
  const narrow = useIsMobile();                           // 모바일: 폴더=드로어, 목록↔상세 드릴다운
  const [folderDrawerOpen, setFolderDrawerOpen] = useState(false);
  // 데스크톱 폴더 패널 폭 — folderPanelW = 사용자 선호(저장값) · panelRowW = [폴더 | 목록·상세] 행의 실제 폭
  const [folderPanelW, setFolderPanelW] = useState(readFolderPanelW);
  const [panelRowW, setPanelRowW] = useState(0);
  const panelRowRef = useRef(null);
  const panelRowRoRef = useRef(null);
  const folderDragCleanupRef = useRef(null);   // 끄는 도중 언마운트되면 document 리스너·오버레이를 걷는다
  const folderPanelShownW = clampFolderPanelW(folderPanelW, panelRowW);   // 화면 표시값(저장값은 덮지 않는다)
  // 행 ref — 붙는 즉시 폭을 재고 ResizeObserver 로 창 크기·확대·메인 사이드바 폭/접기·분할보기 변화를 따라간다
  // (MeetingDetail setSplitNode 와 같은 방식). 🔴 window resize 로 대신하지 않는다 — 사이드바·분할보기 변화를 못 잡는다.
  const setPanelRowNode = useCallback((node) => {
    panelRowRef.current = node;
    if (panelRowRoRef.current) {
      panelRowRoRef.current.disconnect();
      panelRowRoRef.current = null;
    }
    if (!node) {
      setPanelRowW(0);
      return;
    }
    // 웹 배율(<html> zoom)이 걸리면 rect 는 화면 px, 폭 style 은 CSS px 다 → 행의 배율로 나눠 CSS px 로 든다(jsdom·1.0 은 1).
    const measure = () => {
      const w = node.getBoundingClientRect?.().width;
      setPanelRowW(Number.isFinite(w) && w > 0 ? Math.round(w / scaleOf(w, node.offsetWidth)) : 0);
    };
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      panelRowRoRef.current = ro;
    }
    measure();   // 🔴 관찰을 걸고 바로 한 번 잰다 — RO 의 첫 전달을 기다리면 한 프레임이 제한 없이 나간다.
  }, []);
  useEffect(() => () => { if (folderDragCleanupRef.current) folderDragCleanupRef.current(); }, []);
  // 폴더 행 «⋯» 메뉴 닫기 — 바깥 mousedown · Esc · 스크롤 · 창 resize(고정 위치가 어긋난다). MeetingList 펼침 메뉴와 같은 모양.
  // 🔴 바깥 판정은 «⋯» 단추·패널 ref 둘로 한다 — 패널을 빼면 항목을 누르는 mousedown 에 먼저 닫혀 항목이 안 눌린다.
  // 🔴 초점은 Esc 일 때만 «⋯» 로 돌린다 — 항목 선택 뒤 옮기면 «새 하위 폴더» 입력칸(onBlur = 확정)과 이름 변경 입력이 초점을 잃는다.
  useEffect(() => {
    if (!folderMenu) return undefined;
    const close = () => setFolderMenu(null);
    const inMenu = (t) => !!(folderMenuBtnRef.current?.contains(t) || folderMenuPanelRef.current?.contains(t));
    const onDown = (e) => { if (!inMenu(e.target)) close(); };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const btn = folderMenuBtnRef.current;
      close();
      if (btn) btn.focus();
    };
    const onScroll = (e) => { if (!folderMenuPanelRef.current?.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [folderMenu]);
  // P2: 오디오 플레이어(타임스탬프 싱크) · 요약 · 전사/화자 편집
  const [audioUrl, setAudioUrl] = useState(null);
  // 오디오 내려받기 실패 사유 — null | 'gone'(410 = 음성 파일이 서버에 없다) | 'failed'(그 밖)
  // 🔴 이 값이 없으면 재생바가 '받는 중'과 '실패'를 가르지 못해 «불러오는 중»에 영구히 멈춘다.
  const [audioError, setAudioError] = useState(null);
  const [audioRetry, setAudioRetry] = useState(0);   // [다시 시도] 횟수 — 오디오 effect 를 다시 돌리는 열쇠
  const audioRef = useRef(null);
  const [sumMode, setSumMode] = useState('default');
  const sumModeMeetingRef = useRef(null);
  const sumModeManualRef = useRef(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [summarizingId, setSummarizingId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editSegs, setEditSegs] = useState([]);
  const [spkMap, setSpkMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [markSaving, setMarkSaving] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);   // 녹음 메모 저장 중(상세 «메모» 탭)
  // P3: 공유·이동
  const [shareFolder, setShareFolder] = useState(null);
  const [shares, setShares] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  // 여러 개 이동 — 목록이 넘긴 id 목록. 값이 있으면 이동 모달이 '묶음 모드'로 열린다.
  const [bulkMoveIds, setBulkMoveIds] = useState(null);
  const bulkMoveResolveRef = useRef(null);   // MeetingList 에 돌려줄 Promise<boolean> resolver
  // P4: 휴지통 + 관리(감사·사용량)
  const [trashView, setTrashView] = useState(false);
  const [trash, setTrash] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashError, setTrashError] = useState('');
  const [adminView, setAdminView] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [usage, setUsage] = useState([]);
  const [nowMs, setNowMs] = useState(Date.now());
  const [playSec, setPlaySec] = useState(0);   // 현재 재생 위치(초) — 전사 하이라이트용
  const narrowRef = useRef(narrow);
  activeRef.current = active;
  narrowRef.current = narrow;
  foldersRef.current = folders;

  // 받은 목록을 돌려준다(실패면 null) — ?folder= 판정은 자기가 부른 읽기의 응답으로 한다(아래).
  const loadFolders = useCallback(() => {
    setFoldersLoading(true);
    setFoldersError('');
    const p = meetingAPI.listFolders()
      .then((r) => { setFolders(r.data); setFoldersLoaded(true); return r.data; })
      .catch(() => { setFoldersError('폴더 목록을 불러오지 못했습니다.'); return null; })
      .finally(() => setFoldersLoading(false));
    foldersPromiseRef.current = p;
    return p;
  }, []);
  // 개수(stats)는 실패해도 화면을 멈추지 않는다 — 숫자만 숨긴다(오류 문구 없음).
  const loadStats = useCallback(() => {
    const seq = statsSeqRef.current + 1;
    statsSeqRef.current = seq;
    return meetingAPI.stats()
      .then((r) => { if (statsSeqRef.current === seq) setStats(r.data); })
      .catch(() => { if (statsSeqRef.current === seq) setStats(null); });
  }, []);
  const loadMeetings = useCallback(() => {
    const seq = meetingsSeqRef.current + 1;
    meetingsSeqRef.current = seq;
    setMeetingsLoading(true);
    setMeetingsError('');
    // 전체 노트 = scope 'all' · 폴더 = folderId · 미분류 = 둘 다 없음(함께 보내면 400).
    const target = allView ? { scope: 'all' } : (sel?.id ? { folderId: sel.id } : {});
    // 검색어는 ref 로 읽는다(위 listQuery 주석) — 4초 폴링·업로드 뒤 다시 읽기도 같은 검색어를 유지한다.
    const qText = listQueryRef.current;
    // 폴더를 열면 그 폴더에 직접 든 노트만 보인다. 검색어가 있고 그 폴더에 하위 폴더가 있으면
    // 하위 포함 목록(subtree)에서 거른다 — 검색은 서버(q)가 하므로 subtree 를 함께 싣는다.
    const withSub = !allView && sel?.id != null && !!qText
      && (foldersRef.current || []).some((f) => f?.parent_id != null && String(f.parent_id) === String(sel.id));
    return meetingAPI.list({ ...target, sort, order, ...(qText ? { q: qText } : {}), ...(withSub ? { subtree: true } : {}) })
      .then((r) => {
        if (meetingsSeqRef.current !== seq) return;
        setMeetings(r.data);
        setMeetingsQuery(qText);
      })
      .catch(() => {
        if (meetingsSeqRef.current !== seq) return;
        setMeetings([]); setMeetingsError('노트 목록을 불러오지 못했습니다.');
      })
      .finally(() => { if (meetingsSeqRef.current === seq) setMeetingsLoading(false); });
  }, [allView, order, sel, sort]);
  const changeSort = useCallback((nextSort, nextOrder) => {
    setSort(nextSort || 'updated');
    setOrder(nextOrder === 'asc' ? 'asc' : 'desc');
  }, []);
  const pushMeetingDetailHistory = useCallback(() => {
    if (!narrowRef.current || typeof window === 'undefined') return;
    if (window.history.state?.gwMeetingDetail) return;
    window.history.pushState({ gwMeetingDetail: true }, '');
  }, []);
  const closeMeetingDetail = useCallback(() => {
    const hadActive = !!activeRef.current;
    activeRef.current = null;
    setActive(null);
    if (hadActive && narrowRef.current && typeof window !== 'undefined' && window.history.state?.gwMeetingDetail) {
      window.history.back();   // 뒤로가면 URL 도 ?open 없던 항목으로 돌아간다
    } else if (hadActive && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.delete('open');
      const qs = params.toString();
      window.history.replaceState(window.history.state, '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`);
    }
  }, []);
  const setActiveFromDetail = useCallback((nextActive) => {
    if (nextActive === null) {
      closeMeetingDetail();
      return;
    }
    if (typeof nextActive === 'function') {
      setActive((current) => {
        const resolved = nextActive(current);
        activeRef.current = resolved;
        return resolved;
      });
      return;
    }
    activeRef.current = nextActive;
    setActive(nextActive);
  }, [closeMeetingDetail]);

  useEffect(() => {
    // 진입 가드 = 백엔드 can_see_menu와 동일 기준(menu_access 명시 OFF가 아니면 허용, 운영/시스템 관리자는 허용).
    // 하드코딩 @ourbox/admin 제거 → 관리자가 계정별 부여한 권한과 일치(라우트 MenuRoute와도 동일).
    if (!canSeeMenu(user, 'meetings')) navigate('/');
  }, []);

  // 녹음 중 또는 변환 중이면 1초마다 경과시간(nowMs) 갱신.
  // recording을 빼면 동시 전사가 끝나는 순간 busy=false가 되어 녹음 타이머가 멈춤(#138).
  useEffect(() => {
    const busy = recording || recFinalizing || isSttBusy(active) || meetings.some(isSttBusy);
    if (!busy) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active, meetings, recFinalizing, recording]);

  useEffect(() => { loadFolders(); loadStats(); }, [loadFolders, loadStats]);
  useEffect(() => { loadMeetings(); closeMeetingDetail(); }, [loadMeetings, closeMeetingDetail]);
  // 노트 검색 — 입력이 300ms 멈추면 목록을 다시 읽는다(상세는 닫지 않는다 · loadMeetings 를 직접 부른다).
  // 보내기 전에 제어문자(엑셀에서 붙여넣은 탭 등)를 공백으로 바꾼다 — 서버는 제어문자에 400 을 낸다.
  // 보기 전환으로 loadMeetings 만 바뀌고 검색어가 같으면 추가 요청이 없다(위 effect 가 이미 부른다).
  useEffect(() => {
    const t = setTimeout(() => {
      const next = cleanSearchText(listQuery);
      if (next === listQueryRef.current) return;
      listQueryRef.current = next;
      loadMeetings();
    }, 300);
    return () => clearTimeout(t);
  }, [listQuery, loadMeetings]);
  // 녹음 저장은 업로드 함수를 거치지 않는다(RecordingContext 가 업로드) → 여기서 개수도 다시 읽는다.
  useEffect(() => { if (uploadVersion > 0) { loadMeetings(); loadStats(); } }, [loadMeetings, loadStats, uploadVersion]);
  // 🔴 «새 노트»는 **녹음 화면이 열릴 때** 닫는다 — [녹음 시작] 직후에 닫으면 마이크 권한 거부 등으로
  //    시작이 실패했을 때 적어 둔 제목·참석자가 사라진다.
  useEffect(() => { if (recordingScreenOpen) setNewNoteOpen(false); }, [recordingScreenOpen]);

  // 처리 중 회의록이 있으면 4초마다 목록·상세 새로고침.
  // 🔴 목록만 보면 안 된다 — 연 노트가 지금 목록에 없거나(통합검색 ?open=<id> 로 폴더 안 노트를 연 경우)
  //    목록이 상세보다 먼저 '완료'를 받으면 interval 이 풀려 상세가 '변환 중'으로 굳는다.
  useEffect(() => {
    const busy = meetings.some(isSttBusy) || isSttBusy(active);
    if (!busy) return;
    const t = setInterval(() => {
      loadMeetings();
      if (isSttBusy(active)) {
        meetingAPI.get(active.id).then((r) => setActive(r.data)).catch(() => {});
      }
    }, 4000);
    return () => clearInterval(t);
  }, [meetings, active, loadMeetings]);

  // 열려 있는 노트를 URL(?open=<id>)에 남긴다. 안 남기면 새로고침·복귀 시 목록으로 튕겨
  // 보던 자리를 잃는다(마스터 검수 2026-08-14).
  const syncOpenParam = useCallback((meetingId) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (meetingId) params.set('open', String(meetingId));
    else params.delete('open');
    const qs = params.toString();
    window.history.replaceState(window.history.state, '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`);
  }, []);

  const openMeeting = useCallback((m) => meetingAPI.get(m.id).then((r) => {
    activeRef.current = r.data;
    setActive(r.data);
    pushMeetingDetailHistory();
    syncOpenParam(r.data?.id ?? m.id);
  }).catch(() => {}), [pushMeetingDetailHistory, syncOpenParam]);

  useEffect(() => {
    if (!narrow) return undefined;
    const onPopState = () => {
      if (!activeRef.current) return;
      activeRef.current = null;
      setActive(null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [narrow]);

  // 🔴 값이 아니라 이동 한 번(location.key)마다 한 번만 판정한다(아래 ?folder= 와 같다) — 노트를 닫으면
  //    주소창에서만 open 이 지워지고 라우터 값은 그대로라, 값으로 막으면 닫은 노트로 다시 이동했을 때(통합검색) 무반응이다.
  useEffect(() => {
    if (openParamKeyRef.current === location.key) return;
    openParamKeyRef.current = location.key;
    // 라우터 값이 먼저다(같은 화면에서 navigate 로 온 경우) — 없으면 지금 주소창.
    const raw = openParam || new URLSearchParams(window.location.search).get('open');
    if (!raw) return;
    const meetingId = Number(raw);
    if (!Number.isInteger(meetingId) || meetingId <= 0) return;
    folderReqRef.current += 1;   // 기다리던 ?folder= 판정을 버린다 — 늦게 오면 selectFolder 가 이 노트를 닫는다
    openMeeting({ id: meetingId });
  }, [openMeeting, openParam, location.key]);

  // STT 시작(대기/실패/중지됨 → 변환 큐 투입). 시작 후엔 워커가 자동 전사(폴링 새로고침).
  // 🔴 화자분리는 **그 노트에 저장된 값**을 그대로 다시 보낸다(서버 `_meeting_dict.diarize`) —
  //    사용자가 볼 수 없는 세션 기본값으로 노트의 값을 덮으면 안 된다(클로바 묶음 B4c-2).
  const startStt = async (m) => {
    setSttStarting(true);
    try {
      await meetingAPI.update(m.id, { diarize: !!m.diarize });
      const r = await meetingAPI.transcribe(m.id);
      setActive(r.data);
      loadMeetings();
    }
    catch (e) { alertDialog('STT 시작 실패: ' + (e.response?.data?.detail || e.message)); }
    setSttStarting(false);
  };

  // #141 STT 전사 중지(대기열=즉시 중지 / 변환 중=중지 요청→워커 감지). 폴링이 cancelling→cancelled 반영.
  // 변환 중·화자 분석 중은 전사가 화면에서 사라지므로 묻는다(C3c). 대기(uploaded)·화자분리 다시 실행(rediarize · 전사 보존)은 묻지 않는다.
  const cancelStt = async (m) => {
    if ((m.status === 'processing' || m.status === 'diarizing')
      && !(await confirmDialog('변환을 중지할까요? 지금까지 변환한 내용은 보이지 않게 되고, 다시 변환하면 처음부터 다시 받아씁니다.'))) return;
    try { const r = await meetingAPI.cancelTranscribe(m.id); setActive((a) => (a && a.id === m.id ? r.data : a)); loadMeetings(); }
    catch (e) { alertDialog('중지 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  const requestRediarize = async (m) => {
    if (!m || rediarizing) return;
    if (!(await confirmDialog('기존 전사 텍스트는 유지하고 화자분리만 다시 실행할까요?'))) return;
    setRediarizing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/meetings/${m.id}/diarize`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || '화자분리 요청 실패');
      setActive(data);
      loadMeetings();
    } catch (e) {
      alertDialog('화자분리 요청 실패: ' + e.message);
    } finally {
      setRediarizing(false);
    }
  };

  // 회의록 이름(제목) 수정
  const renameMeeting = async (m) => {
    const t = await promptDialog('노트 이름', m.title);
    if (t == null) return;
    const name = t.trim(); if (!name) return;
    try { const r = await meetingAPI.update(m.id, { title: name }); setActive(r.data); loadMeetings(); }
    catch (e) { alertDialog('이름 변경 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  // 폴더 요약양식 편집(비우면 기본양식 사용)
  const openFolderPrompt = () => { setFpText(sel?.summary_prompt || ''); setFpEdit(true); };
  const saveFolderPrompt = async () => {
    if (!sel) return;
    try {
      const r = await meetingAPI.updateFolder(sel.id, { summary_prompt: fpText });
      setSel((s) => ({ ...s, summary_prompt: r.data.summary_prompt }));
      loadFolders(); setFpEdit(false);
    } catch (e) { alertDialog('양식 저장 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  const activeMeetingId = active?.id ?? null;
  const activeFolderId = active?.folder_id ?? null;
  // 오디오를 받을 수 있는 상태인가 — 상세를 연 채 변환이 끝나면 id 는 그대로고 이 값만 false→true 가 된다.
  const audioReady = !!(active && active.status === 'done' && active.has_audio);

  useEffect(() => {
    if (activeMeetingId === null || activeMeetingId === undefined) {
      sumModeMeetingRef.current = null;
      sumModeManualRef.current = false;
      return;
    }
    const meetingKey = String(activeMeetingId);
    const meetingChanged = sumModeMeetingRef.current !== meetingKey;
    if (sumModeManualRef.current && !meetingChanged) return;
    if (!meetingChanged) return;
    sumModeMeetingRef.current = meetingKey;
    sumModeManualRef.current = false;
    setSumMode(summaryModeDefaultForFolder(folders, activeFolderId));
  }, [activeMeetingId, activeFolderId, folders]);

  const setSumModeFromUser = useCallback((mode) => {
    sumModeManualRef.current = true;
    setSumMode(mode);
  }, []);

  // 회의록 전환 시 편집모드 해제(노트를 바꿀 때만 — 상태가 바뀐다고 편집이 풀리면 안 된다)
  // 🔴 재생 위치도 0 으로 — 안 비우면 새 노트를 재생하기 전에 남긴 메모가 앞 노트의 시각에 박힌다.
  useEffect(() => {
    setEditing(false);
    setPlaySec(0);
  }, [active?.id]);

  // 오디오 blob 로드(타임스탬프 싱크용).
  // 🔴 의존성에 audioReady 가 있어야 한다 — 상세를 연 채 변환이 끝나면 4초 폴링이 **같은 id** 로
  //    setActive 하므로, [active?.id] 만으로는 effect 가 다시 돌지 않아 재생바가 '불러오는 중'에 멈춘다.
  // 🔴 실패를 삼키면(옛 빈 catch) audioUrl 이 null 그대로라 재생바가 «불러오는 중»에 영구히 멈춘다
  //    → 사유를 남겨 재생바가 한 줄로 알리게 한다(410 = 서버에 파일 없음, 그 밖 = 다시 시도 가능).
  useEffect(() => {
    if (!audioReady) { setAudioUrl(null); return; }
    let url = null; let cancelled = false;
    meetingAPI.downloadAudio(active.id)
      .then((r) => { if (!cancelled) { url = URL.createObjectURL(r.data); setAudioUrl(url); } })
      // 🔴 성공 경로와 같은 cancelled 가드 — 늦게 온 이전 노트의 실패가 새 노트에 뜨면 안 된다.
      .catch((e) => { if (!cancelled) setAudioError(e?.response?.status === 410 ? 'gone' : 'failed'); });
    // 정리에서 사유를 비운다 — 노트 전환·다시 시도·오디오 없어짐 모두 이 한 곳을 지난다.
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); setAudioUrl(null); setAudioError(null); };
  }, [active?.id, audioReady, audioRetry]);

  const retryAudio = useCallback(() => setAudioRetry((n) => n + 1), []);

  const seekTo = (sec) => { const a = audioRef.current; if (!a) return; a.currentTime = sec || 0; a.play().catch(() => {}); };

  const SUMMARY_LONG = 60000;   // 이 길이(글자) 초과 시 전체/앞부분 선택을 물음(백엔드 SUMMARY_HEAD_LIMIT과 일치)
  const genSummary = async (scope) => {
    if (!active) return;
    const meetingId = active.id;
    if (sumMode === 'custom' && !customPrompt.trim()) { alertDialog('직접 지시 내용을 입력하세요.'); return; }
    const len = (active.transcript_text || '').length;
    if (scope === undefined && len > SUMMARY_LONG) { setLongAsk(true); return; }   // 길면 선택 모달
    setLongAsk(false);
    setSummarizingId(meetingId);
    try {
      const r = await meetingAPI.summarize(meetingId, { mode: sumMode, custom_prompt: customPrompt, scope: scope || 'head' });
      // summary_falsify_warn: 반증 ⚠ 한 줄(요약 본문 밖 · 저장 안 함) — 새 요약마다 덮어 이전 줄이 남지 않게.
      setActive((a) => (a && a.id === meetingId ? { ...a, summary: r.data.summary, summary_prompt_used: r.data.summary_prompt_used, has_summary: true, keywords: r.data.keywords, summary_falsify_warn: r.data.falsify_warn || '' } : a));
    } catch (e) { alertDialog('요약 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setSummarizingId((id) => (id === meetingId ? null : id)); }
  };

  const startEdit = () => {
    const segs = Array.isArray(active.transcript) ? active.transcript : [];
    setEditSegs(segs.map((s) => ({ ...s })));
    const m = {}; segs.forEach((s) => { if (s.speaker && !(s.speaker in m)) m[s.speaker] = s.speaker; });
    setSpkMap(m); setEditing(true);
  };
  const transcriptPayload = (segs) => segs.map((s) => {
    const item = { start: s.start, end: s.end, speaker: s.speaker || null, text: s.text || '' };
    if (s.bookmark) item.bookmark = true;
    if (s.highlight) item.highlight = true;
    const memo = (s.memo || '').trim();
    if (memo) item.memo = memo.slice(0, 1000);
    // 낱말 시각(S3) — 그대로 실어 보낸다. 글자를 고친 발화의 낡은 words 는 서버가 글자·시각 규칙으로 버린다.
    if (Array.isArray(s.words) && s.words.length) item.words = s.words;
    return item;
  });
  const saveEdit = async () => {
    setSaving(true);
    try {
      const segs = editSegs.map((s) => ({ ...s, speaker: s.speaker ? (spkMap[s.speaker] ?? s.speaker) : null }));
      const r = await meetingAPI.update(active.id, { transcript: transcriptPayload(segs) });
      setActive(r.data); setEditing(false); loadMeetings();
    } catch (e) { alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  // 🔴 저장 응답으로 노트 전체(`r.data`)를 덮지 않는다 — 그 저장이 바꾼 칸만 합친다.
  //    서버는 transcript·recording_notes 를 각각 통째 교체하므로, 통째로 덮으면 메모 저장과
  //    표식 저장이 겹칠 때 늦게 온 응답이 다른 쪽 변경을 화면에서 지우고(그 값으로 다음 저장을
  //    하면 서버에서도 지워진다). updated_at 은 머리 메타 줄이 낡지 않게 함께 합친다.
  const saveTranscriptMarks = async (nextSegs) => {
    if (!active || markSaving) return;
    const id = active.id;
    const prev = active;
    setMarkSaving(true);
    setActive((a) => (a && a.id === id ? { ...a, transcript: nextSegs } : a));
    try {
      const r = await meetingAPI.update(id, { transcript: transcriptPayload(nextSegs) });
      const savedSegs = Array.isArray(r.data?.transcript) ? r.data.transcript : nextSegs;
      setActive((a) => (a && a.id === id ? {
        ...a,
        transcript: savedSegs,
        transcript_text: r.data?.transcript_text ?? a.transcript_text,
        updated_at: r.data?.updated_at ?? a.updated_at,
      } : a));
      loadMeetings();
    } catch (e) {
      setActive((a) => (a && a.id === id ? { ...a, transcript: prev.transcript } : a));
      alertDialog('표시 저장 실패: ' + (e.response?.data?.detail || e.message));
    } finally { setMarkSaving(false); }
  };
  // 녹음 메모 저장(상세 «메모» 탭의 추가·삭제) — PATCH recording_notes.
  // 권한이 없으면 서버가 403 «편집 권한이 없습니다.» 를 주고 그 글을 그대로 알린다.
  const saveRecordingNotes = async (nextNotes) => {
    if (!active || notesSaving) return false;
    const id = active.id;
    const prevNotes = Array.isArray(active.recording_notes) ? active.recording_notes : [];
    setNotesSaving(true);
    setActive((a) => (a && a.id === id ? { ...a, recording_notes: nextNotes } : a));
    try {
      const r = await meetingAPI.update(id, { recording_notes: nextNotes });
      const saved = Array.isArray(r.data?.recording_notes) ? r.data.recording_notes : nextNotes;
      setActive((a) => (a && a.id === id ? { ...a, recording_notes: saved, updated_at: r.data?.updated_at ?? a.updated_at } : a));
      loadMeetings();
      return true;
    } catch (e) {
      setActive((a) => (a && a.id === id ? { ...a, recording_notes: prevNotes } : a));
      alertDialog('메모 저장 실패: ' + (e.response?.data?.detail || e.message));
      return false;
    } finally { setNotesSaving(false); }
  };
  const toggleSegFlag = (idx, field) => {
    if (editing) return;
    const segs = Array.isArray(active?.transcript) ? active.transcript.map((s) => ({ ...s })) : [];
    if (!segs[idx]) return;
    if (segs[idx][field]) delete segs[idx][field]; else segs[idx][field] = true;
    saveTranscriptMarks(segs);
  };
  const editSegMemo = async (idx) => {
    if (editing) return;
    const segs = Array.isArray(active?.transcript) ? active.transcript.map((s) => ({ ...s })) : [];
    if (!segs[idx]) return;
    const memo = await promptDialog('메모', segs[idx].memo || '');
    if (memo == null) return;
    const text = memo.trim();
    if (text) segs[idx].memo = text; else delete segs[idx].memo;
    saveTranscriptMarks(segs);
  };

  // P3 핸들러
  const openShare = async (f) => {
    setShareFolder(f); setUserQuery(''); setUserResults([]);
    try { const r = await meetingAPI.listShares(f.id); setShares(r.data); } catch { setShares([]); }
  };
  const doSearchUsers = async (q) => {
    setUserQuery(q);
    if (!q.trim()) { setUserResults([]); return; }
    try { const r = await meetingAPI.searchUsers(q.trim()); setUserResults(r.data); } catch { setUserResults([]); }
  };
  const addShare = async (u, role) => {
    try { const r = await meetingAPI.addShare(shareFolder.id, { user_id: u.id, role }); setShares(r.data); setUserQuery(''); setUserResults([]); }
    catch (e) { alertDialog('공유 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const removeShare = async (uid) => {
    try { await meetingAPI.removeShare(shareFolder.id, uid); setShares((s) => s.filter((x) => x.user_id !== uid)); } catch { alertDialog('해제 실패'); }
  };
  // 노트 상세의 폴더 변경 — 여러 개 이동과 같은 길(moveNotes · ids=[active.id])을 탄다.
  const doMove = async (folderId) => {
    if (!active) return;
    const id = active.id;
    const ok = await moveNotes([id], folderId);
    if (!ok) return;                      // 실패·취소면 모달을 그대로 둔다(다른 폴더로 다시 고를 수 있게)
    setMoveOpen(false);
    setActive((a) => (a && a.id === id ? { ...a, folder_id: folderId } : a));
    // 전체 노트 화면에서는 어느 폴더로 옮겨도 목록에 그대로 남으므로 상세를 닫지 않는다.
    if (!allView && folderId !== (sel?.id ?? null)) closeMeetingDetail();
  };

  // ── 여러 개 처리(목록 체크박스) ──────────────────────────────────────────
  // 콜백은 Promise<boolean> 로 끝난다 — true 면 MeetingList 가 선택을 비운다.
  // 🔴 서버 상한(meetings.BULK_MAX)은 요청 전에 막는다(400 을 보고 뒤늦게 알리지 않는다).
  const BULK_MAX = 200;
  // 묶음 오류 detail 은 문자열이지만 pydantic 422 는 배열이라 그대로 붙이면 [object Object] 가 된다.
  const bulkErrText = (e) => {
    const d = e?.response?.data?.detail;
    return typeof d === 'string' ? d : (e?.message || '알 수 없는 오류');
  };
  const overBulkMax = (n) => {
    if (n <= BULK_MAX) return false;
    alertDialog(`한 번에 최대 ${BULK_MAX}개까지 처리할 수 있습니다.`);
    return true;
  };
  // resolver 는 이 함수로만 끝낸다(두 번 resolve 되지 않게).
  const settleBulkMove = (v) => {
    const resolve = bulkMoveResolveRef.current;
    bulkMoveResolveRef.current = null;
    if (resolve) resolve(v);
  };
  const bulkTrashMeetings = async (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    if (list.length === 0) return false;
    if (overBulkMax(list.length)) return false;
    if (!(await confirmDialog(`선택한 ${list.length}개 노트를 휴지통으로 보낼까요?`))) return false;
    try {
      await meetingAPI.bulkTrash(list);
      loadMeetings(); loadStats();
      return true;
    } catch (e) { alertDialog('삭제 실패: ' + bulkErrText(e)); return false; }
  };
  const bulkMoveMeetings = (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    if (list.length === 0) return Promise.resolve(false);
    if (overBulkMax(list.length)) return Promise.resolve(false);
    settleBulkMove(false);   // 앞서 열어 둔 묶음 이동이 남아 있으면 먼저 끝낸다
    return new Promise((resolve) => {
      bulkMoveResolveRef.current = resolve;
      setBulkMoveIds(list);
    });
  };
  const closeMoveModal = () => {
    setMoveOpen(false);
    setBulkMoveIds(null);
    settleBulkMove(false);
  };
  // 이동 실패 문구 — 서버가 failures([{id,title,status,reason}])를 주면 줄마다 «- 제목: 이유», 없으면 예전 문구.
  const moveErrText = (e) => {
    const failures = e?.response?.data?.failures;
    if (Array.isArray(failures) && failures.length > 0) {
      return '이동 실패:\n' + failures.map((f) => `- ${f?.title || `노트 #${f?.id}`}: ${f?.reason || '알 수 없는 오류'}`).join('\n');
    }
    return '이동 실패: ' + bulkErrText(e);
  };
  // 노트 이동 공통(단건·여러 개) — ① dry_run 으로 먼저 확인 ② 보는 사람이 바뀔 때(share_change)만 확인창
  // ③ 실제 이동은 정확히 bulkMove(ids, folderId) 인자 2개. 취소하면 아무 요청도 더 보내지 않는다.
  // 반환: true = 옮김 · false = 실패(알림)·취소. 🔴 실패해도 모달은 호출부가 그대로 둔다.
  const moveNotes = async (ids, folderId) => {
    try {
      const dry = await meetingAPI.bulkMove(ids, folderId, { dryRun: true });
      const change = dry?.data?.share_change;
      if (change && !(await confirmDialog(shareChangeText(change)))) return false;
      await meetingAPI.bulkMove(ids, folderId);
      loadMeetings(); loadStats();             // 서버가 바뀌었으니 다시 읽는다
      return true;
    } catch (e) {
      alertDialog(moveErrText(e));
      return false;
    }
  };
  const doBulkMove = async (folderId) => {
    const ids = bulkMoveIds;
    if (!Array.isArray(ids) || ids.length === 0) return;
    const mine = bulkMoveResolveRef.current;   // 늦은 응답이 '다음' 모달을 닫지 못하게 잡아 둔다
    // 🔴 실패·취소하면 모달과 Promise 를 그대로 둔다(단건 doMove 와 같다) — 다른 폴더로 다시 시도할 수 있다.
    const ok = await moveNotes(ids, folderId);
    if (!ok) return;
    if (bulkMoveResolveRef.current !== mine) return;
    setMoveOpen(false); setBulkMoveIds(null);
    settleBulkMove(true);
  };

  // P4: 휴지통
  const loadTrash = useCallback(() => {
    setTrashLoading(true);
    setTrashError('');
    // 삭제된 폴더(묶음의 맨 위만) — 실패하면 빈 배열(노트 휴지통은 그대로 보인다).
    Promise.resolve()
      .then(() => meetingAPI.folderTrash())
      .then((r) => setFolderTrash(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setFolderTrash([]));
    return meetingAPI.trash()
      .then((r) => setTrash(r.data))
      .catch(() => { setTrash([]); setTrashError('휴지통을 불러오지 못했습니다.'); })
      .finally(() => setTrashLoading(false));
  }, []);
  useEffect(() => { if (trashView) loadTrash(); }, [trashView, loadTrash]);
  useEffect(() => {
    if (!adminView) return;
    meetingAPI.adminAudit().then((r) => setAuditLog(r.data)).catch(() => setAuditLog([]));
    meetingAPI.adminUsage().then((r) => setUsage(r.data)).catch(() => setUsage([]));
  }, [adminView]);
  const restoreMeeting = async (m) => {
    // #144 복원 직후 상세가 전사를 반영하도록: 휴지통 뷰 이탈 + 해당 회의록 재오픈(GET with_transcript).
    // (백엔드/DB는 정상 — D 재현으로 transcript 보존 확인됨. 복원 응답 _meeting_dict엔 transcript 없어 자동표시만 누락이던 것.)
    try {
      await meetingAPI.restore(m.id);
      loadTrash(); loadMeetings(); loadStats();
      setTrashView(false);
      openMeeting(m);
    } catch { alertDialog('복원 실패'); }
  };
  // 삭제된 폴더 되돌리기 — 구조(함께 지운 하위 폴더)와 그 삭제로 휴지통에 간 노트가 함께 돌아온다.
  const restoreFolder = async (f) => {
    try {
      await meetingAPI.restoreFolder(f.id);
      loadFolders(); loadStats(); loadTrash(); loadMeetings();
    } catch (e) { alertDialog('폴더 복원 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const purgeMeeting = async (m) => {
    if (!(await confirmDialog(`'${m.title}'을(를) 영구 삭제할까요? 복구할 수 없습니다.`, { danger: true }))) return;
    try { await meetingAPI.permanentDelete(m.id); loadTrash(); } catch { alertDialog('삭제 실패'); }
  };
  // 휴지통 여러 개 — 콜백 모양은 bulkTrashMeetings 와 같다(Promise<boolean>).
  // 여러 개 복원은 열 노트가 하나가 아니므로 휴지통에 머문다(한 건 복원만 그 노트를 연다).
  // 🔴 실패해도 휴지통을 다시 읽는다 — /bulk/permanent 는 이미 복원된 노트가 하나라도 섞이면 전체를
  //    400 으로 거부한다. 다시 읽지 않으면 낡은 행이 남아 같은 400 이 되풀이된다.
  const bulkRestoreMeetings = async (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    if (list.length === 0) return false;
    if (overBulkMax(list.length)) return false;
    try {
      await meetingAPI.bulkRestore(list);
      loadTrash(); loadMeetings(); loadStats();
      return true;
    } catch (e) {
      alertDialog('복원 실패: ' + bulkErrText(e));
      await loadTrash();
      return false;
    }
  };
  const bulkPurgeMeetings = async (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    if (list.length === 0) return false;
    if (overBulkMax(list.length)) return false;
    if (!(await confirmDialog(`선택한 ${list.length}개 노트를 영구 삭제할까요? 복구할 수 없습니다.`, { danger: true }))) return false;
    try {
      await meetingAPI.bulkPermanent(list);
      loadTrash();
      return true;
    } catch (e) {
      alertDialog('삭제 실패: ' + bulkErrText(e));
      await loadTrash();
      return false;
    }
  };
  const selectFolder = (f) => { setHomeView(false); setSel(f); setAllView(false); setTrashView(false); setAdminView(false); closeMeetingDetail(); setNewNoteOpen(false); setFolderDrawerOpen(false); };
  const selectAllNotes = () => { setHomeView(false); setSel(null); setAllView(true); setTrashView(false); setAdminView(false); closeMeetingDetail(); setNewNoteOpen(false); setFolderDrawerOpen(false); };
  const selectHome = () => { setHomeView(true); setSel(null); setAllView(true); setTrashView(false); setAdminView(false); closeMeetingDetail(); setNewNoteOpen(false); setFolderDrawerOpen(false); };

  // 폴더 공유 알림(?folder=<id>) — 폴더 목록을 받은 뒤 판정한다(공유받은 폴더도 같은 folders 배열에 든다).
  // 찾으면 그 폴더를 열고, 찾든 못 찾든 주소에서 folder 를 지운다. ?open= 이 함께 있으면 folder 는 무시한다.
  // 🔴 값이 아니라 이동 한 번(location.key)마다 한 번만 판정한다 — 주소창에서 지워도 라우터 값은 그대로라
  //    값으로 막으면 같은 알림을 또 눌렀을 때 무반응이고, 막지 않으면 폴더 목록을 다시 읽을 때마다 그 폴더로 끌려간다.
  // 🔴 이미 이 화면에 있으면(같은 Route — 다시 마운트되지 않는다) 들고 있는 폴더 목록은 화면을 연 때 것이다 —
  //    폴더 목록은 주기적으로 다시 읽지 않으니 그 뒤에 공유받은 폴더가 없다. 그래서 목록을 다시 읽고 **그 응답으로** 판정한다.
  //    첫 진입은 마운트 때 시작한 읽기를 기다리고(같은 요청 두 번 X), 거기서 못 찾으면 한 번만 더 읽는다.
  // 🔴 늦게 온 판정은 «판정 번호»(folderReqRef)로 버린다 — 새 ?folder=·?open= 이 오면 번호가 바뀐다.
  //    이동(location.key)이 바뀌었다고 버리지 않는다: 폴더와 무관한 이동 한 번에 판정이 통째로 사라져
  //    그 폴더가 안 열리고 주소에 folder 가 남는다(시험 환경 실례: resetMocks 가 menuAccess 목을 지워
  //    마운트 때 navigate('/') 가 한 번 돈다 — location.key 로 막았을 때 첫 진입 ?folder= 시험 3건이 그래서 깨졌다).
  const folderParamKeyRef = useRef(null);
  useEffect(() => {
    if (!folderParam || folderParamKeyRef.current === location.key) return;
    folderParamKeyRef.current = location.key;
    const wanted = String(folderParam);
    const req = folderReqRef.current + 1;
    folderReqRef.current = req;
    const clearFolderParam = () => {
      const params = new URLSearchParams(window.location.search);
      params.delete('folder');
      const qs = params.toString();
      window.history.replaceState(window.history.state, '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`);
    };
    if (openParam) { clearFolderParam(); return; }
    const settle = (list, retry) => {
      if (folderReqRef.current !== req) return;   // 그 사이 새 ?folder=·?open= 이 왔다 — 그쪽이 판정한다
      const target = (list || []).find((f) => String(f.id) === wanted);
      if (!target && retry) { loadFolders().then((fresh) => settle(fresh, false)); return; }
      if (target) selectFolder(target);
      clearFolderParam();
    };
    const firstLoad = !foldersLoaded && foldersPromiseRef.current;
    if (firstLoad) firstLoad.then((list) => settle(list, true));
    else loadFolders().then((list) => settle(list, false));
  }, [folderParam, openParam, foldersLoaded, location.key, loadFolders]);

  // 폴더 추가 — 「전체 노트」 행의 «+» → 폴더 목록 맨 위 입력칸(옛 promptDialog 대체)
  const openNewFolder = () => { folderCreateRef.current = false; setNewFolderName(''); setNewFolderOpen(true); };
  const closeNewFolder = () => { folderCreateRef.current = true; setNewFolderOpen(false); setNewFolderName(''); };
  const commitNewFolder = async (raw) => {
    if (folderCreateRef.current) return;   // 이미 처리한 입력칸의 뒤늦은 blur·중복 Enter 무시
    const name = String(raw ?? '').trim();
    if (!name) { closeNewFolder(); return; }
    folderCreateRef.current = true;        // 다음 «+» (openNewFolder) 까지 잠금 유지
    setNewFolderOpen(false);
    setNewFolderName('');
    try {
      const r = await meetingAPI.createFolder({ name });
      loadFolders(); loadStats();
      setHomeView(false); setSel(r.data); setAllView(false); setTrashView(false); setAdminView(false);
    } catch (e) { alertDialog('생성 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  // 폴더 펼침 — localStorage(gw.meetings.folderExpanded)에 남겨 새로고침해도 유지한다.
  const expandFolder = useCallback((id) => {
    if (id === null || id === undefined) return;
    const key = String(id);
    setExpandedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      writeExpanded(next);
      return next;
    });
  }, []);
  const toggleExpanded = (key) => setExpandedKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    writeExpanded(next);
    return next;
  });
  // 하위 폴더 추가 — 그 폴더 행 바로 아래 «새 하위 폴더» 입력칸(1단계 «새 폴더» 입력칸과 같은 잠금 규칙).
  const openChildFolder = (f) => {
    childCreateRef.current = false;
    setChildFolderName('');
    setChildFolderParent(f.id);
    expandFolder(f.id);
  };
  const closeChildFolder = () => { childCreateRef.current = true; setChildFolderParent(null); setChildFolderName(''); };
  const commitChildFolder = async (raw) => {
    if (childCreateRef.current) return;
    const parentId = childFolderParent;
    const name = String(raw ?? '').trim();
    if (!name || parentId === null || parentId === undefined) { closeChildFolder(); return; }
    childCreateRef.current = true;
    setChildFolderParent(null);
    setChildFolderName('');
    try {
      const r = await meetingAPI.createFolder({ name, parent_id: parentId });
      expandFolder(parentId);
      loadFolders(); loadStats();
      setHomeView(false); setSel(r.data); setAllView(false); setTrashView(false); setAdminView(false);
    } catch (e) { alertDialog('생성 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const renameFolder = async (f) => {
    const t = await promptDialog('폴더 이름', f.name);
    if (t == null) return;
    const name = t.trim();
    if (!name || name === f.name) return;
    try {
      const r = await meetingAPI.updateFolder(f.id, { name });
      loadFolders();
      if (sel?.id === f.id) setSel((s) => (s ? { ...s, name: r.data?.name ?? name } : s));
    } catch (e) { alertDialog('이름 변경 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  // 폴더 위치 옮기기 — dry_run → share_change 가 있으면 확인창 → 실제 요청. 서버 오류 detail 은 그대로 알린다.
  const doFolderMove = async (parentId) => {
    const f = folderMove;
    if (!f) return;
    try {
      const dry = await meetingAPI.moveFolder(f.id, parentId, { dryRun: true });
      const change = dry?.data?.share_change;
      if (change && !(await confirmDialog(shareChangeText(change)))) return;
      await meetingAPI.moveFolder(f.id, parentId);
      if (parentId !== null && parentId !== undefined) expandFolder(parentId);
      setFolderMove(null);
      loadFolders(); loadStats();
    } catch (e) { alertDialog(bulkErrText(e)); }
  };
  // 폴더 삭제 — 서버 미리보기(하위 폴더·노트 수, 작성자 미분류로 갈 남의 노트 수)로 확인 문구를 만든다.
  // 미리보기를 못 받으면 예전 문구(folderDeleteMessage 가 고른다).
  const delFolder = async (f) => {
    let preview = null;
    try {
      const r = await meetingAPI.folderDeletePreview(f.id);
      preview = r?.data || null;
    } catch { preview = null; }
    if (!(await confirmDialog(folderDeleteMessage(f.name, preview)))) return;
    try {
      await meetingAPI.deleteFolder(f.id);
      // 보던 폴더가 지운 트리 안에 있으면 전체 노트로. 전체 노트 화면이면 지운 폴더의 노트가 목록에 남으므로 다시 읽는다.
      if (sel?.id != null && (sel.id === f.id || isInSubtree(folders, f.id, sel.id))) { setSel(null); setAllView(true); }
      loadFolders(); loadStats(); loadMeetings();
    } catch (e) {
      const d = e?.response?.data?.detail;
      alertDialog(typeof d === 'string' ? `삭제 실패: ${d}` : '삭제 실패');
    }
  };

  // 업로드 공통 — 진행률(%) 표시 + 버튼 잠금
  // meta = «새 노트» 화면이 넘긴 값(폴더·화자분리·참석자·인식 언어).
  // null 은 지금 호출자가 없다(«노트 만들기» 단추 삭제 · 클로바 묶음 B4c-2) — 새 노트 화면만 meta 를 넘긴다.
  const uploadBlob = async (file, filename, title, recordingNotesForUpload, source = 'upload', meta = null) => {
    const fd = new FormData();
    fd.append('file', file, filename);
    const folderId = meta ? meta.folderId : (sel ? sel.id : null);
    if (folderId !== null && folderId !== undefined) fd.append('folder_id', folderId);
    fd.append('title', title);
    fd.append('source', source);
    if (Array.isArray(recordingNotesForUpload)) {
      fd.append('recording_notes', JSON.stringify(recordingNotesForUpload));
    }
    fd.append('diarize', (meta ? meta.diarize : diarizeUpload) ? 'true' : 'false');
    if (meta) {
      // 비어 있으면 키를 넣지 않는다 — 서버는 안 보낸 값을 NULL 로 둔다.
      if (Array.isArray(meta.participants) && meta.participants.length) {
        fd.append('participants', JSON.stringify(meta.participants));
      }
      if (meta.language) fd.append('language', meta.language);
    }
    const uploadId = fileUploadSeqRef.current + 1;
    fileUploadSeqRef.current = uploadId;
    const setActiveFileUploadPct = (next) => {
      if (fileUploadSeqRef.current === uploadId) setFileUploadPct(next);
    };
    setActiveFileUploadPct(0);
    try {
      await meetingAPI.upload(fd, (e) => {
        if (e.total) setActiveFileUploadPct(Math.round((e.loaded / e.total) * 100));
      });
      loadMeetings(); loadStats();
      return true;
    } catch (e) {
      alertDialog('업로드 실패: ' + (e.response?.data?.detail || e.message));
      return false;
    }
    finally {
      if (fileUploadSeqRef.current === uploadId) {
        fileUploadSeqRef.current += 1;
        setFileUploadPct(null);
      }
    }
  };
  // «녹음하기»(즉시 녹음) — 화자분리는 새 노트 화면에서 **녹음을 시작하거나 파일을 첨부할 때**
  // 고른 값을 세션 기본값으로 쓴다(기본 false · 새 노트를 그냥 닫으면 바뀌지 않는다).
  const startRec = useCallback(() => {
    startRecording({ folderId: sel?.id ?? null, folderName: sel?.name || '', diarize: diarizeUpload });
  }, [diarizeUpload, sel, startRecording]);
  // STT 큐 대기 상황: queue_ahead = 앞에 있는(대기+처리중) 건수
  // 🔴 "N번째"로 쓰지 않는다 — 그건 워커가 1개일 때만 맞다(2026-09-03 마스터 지적).
  //   실제 워커는 2개(ai-service-stt-1/2)라 "2번째"도 곧바로 시작되는데 화면은 줄 서서
  //   기다린다고 말했다. 백엔드가 워커 수를 알 방법이 없으므로(하트비트·표식 컬럼·설정 전부 없음)
  //   설정으로 워커 수를 받으면 실제와 조용히 어긋난다. 그래서 **개수만** 말한다 —
  //   워커가 몇 개든 참인 문장이다.
  const queueText = (m) => {
    if (!m || m.status !== 'uploaded' || m.queue_ahead == null) return '';
    return m.queue_ahead === 0 ? '곧 변환 시작' : `변환 대기 · 앞에 ${m.queue_ahead}건`;
  };

  const delMeeting = async (m) => {
    if (!(await confirmDialog(`'${m.title}' 노트를 휴지통으로 보낼까요?`))) return;
    try { await meetingAPI.remove(m.id); if (active?.id === m.id) closeMeetingDetail(); loadMeetings(); loadStats(); } catch { alertDialog('삭제 실패'); }
  };
  // 파일명은 utils/meetingUtil 의 audioDownloadName·meetingFileBase — 제목 속 점을 확장자로 오인하지 않는다.
  const dlAudio = async (m) => {
    try { const r = await meetingAPI.downloadAudio(m.id);
      const name = audioDownloadName(m.title, m.audio_mime);
      blobDownload(r.data, name); }
    catch (e) { alertDialog(e.response?.status === 410 ? '오디오 보관 기간이 지났습니다.' : '다운로드 실패'); }
  };
  const dlTxt = async (m) => {
    try { const r = await meetingAPI.downloadTranscript(m.id);
      const base = meetingFileBase(m.title, 'transcript');
      blobDownload(r.data, base + '.txt'); }
    catch (e) { alertDialog('변환 결과가 아직 없습니다.'); }
  };
  // 요약 받기 — 형식은 요약 카드 «다운로드 ▾» 가 고른다(docx·md·txt · 2026-10-15). 기본값 docx = 예전 동작.
  // docx 는 서버가 만들고, md·txt 는 화면이 가진 요약(meetings.summary)으로 브라우저가 만든다.
  const dlSummary = async (m, fmt = 'docx') => {
    const base = meetingFileBase(m.title, 'meeting');
    if (fmt === 'md' || fmt === 'txt') {
      if (!String(m.summary ?? '').trim()) { alertDialog('요약이 아직 없습니다.'); return; }
      if (fmt === 'md') {
        blobDownload(new Blob([buildSummaryMarkdown(m)], { type: 'text/markdown;charset=utf-8' }), base + '_요약.md');
      } else {
        const BOM = String.fromCharCode(0xFEFF);
        blobDownload(new Blob([BOM + buildSummaryText(m)], { type: 'text/plain;charset=utf-8' }), base + '_요약.txt');
      }
      return;
    }
    try { const r = await meetingAPI.downloadSummaryDocx(m.id);
      blobDownload(r.data, base + '_요약.docx'); }
    catch (e) { alertDialog('요약이 아직 없습니다.'); }
  };
  // 요약·메모 TXT·MD 는 서버를 거치지 않고 화면이 가진 값으로 만든다(클로바 묶음 C2).
  // 앞에 붙인 BOM(U+FEFF)은 Windows 메모장에서 한글이 깨지지 않게 하는 용도다(TXT 만 · MD 는 BOM 없음).
  // 🔴 소스에 보이지 않는 문자를 박지 않는다 — 코드 포인트로 만든다(String.fromCharCode).
  // DOCX 는 같은 마크다운을 AI노트 라우트(summary-notes.docx)로 보내 바꾼다 — 채팅 export-docx 는 채팅 권한 게이트라 안 쓴다.
  const dlSummaryNotes = async (m, fmt = 'txt') => {
    const base = meetingFileBase(m.title, 'meeting');
    if (fmt === 'md') {
      blobDownload(new Blob([buildSummaryNotesMarkdown(m)], { type: 'text/markdown;charset=utf-8' }), base + '_요약_메모.md');
      return;
    }
    if (fmt === 'docx') {
      try { const r = await meetingAPI.downloadSummaryNotesDocx(m.id, buildSummaryNotesMarkdown(m));
        blobDownload(r.data, base + '_요약_메모.docx'); }
      catch (e) { alertDialog('다운로드 실패'); }
      return;
    }
    const BOM = String.fromCharCode(0xFEFF);
    blobDownload(new Blob([BOM + buildSummaryNotesText(m)], { type: 'text/plain;charset=utf-8' }), base + '_요약_메모.txt');
  };
  const setRagOpt = async (m, enabled) => {
    try {
      const r = await meetingAPI.setRagOpt(m.id, enabled);
      setActive((a) => (a && a.id === m.id ? { ...a, ...r.data } : a));
      setMeetings((rows) => rows.map((row) => (row.id === m.id
        ? { ...row, rag_opt_in: !!r.data.rag_opt_in, updated_at: r.data.updated_at }
        : row)));
    } catch (e) {
      alertDialog('RAG 등록 변경 실패: ' + (e.response?.data?.detail || e.message));
    }
  };

  const spkDisp = speakerDisplayMap(active?.transcript);
  const playIdx = (Array.isArray(active?.transcript) ? active.transcript : []).findIndex((s) => playSec >= s.start && playSec < (s.end || s.start + 1));
  const uploadPct = fileUploadPct;
  const disabledWork = fileUploadPct != null || recordingBusy;
  const recordingUploadItems = Array.isArray(pendingUploads) ? pendingUploads : [];
  const recordingUploadQueue = recordingUploadItems.length > 0 ? (
    <div style={{ flexShrink: 0, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', padding: narrow ? '8px 12px' : '8px 14px', borderBottom: `1px solid ${T.color.borderSoft}`, background: T.color.surface }}>
      {recordingUploadItems.map((item) => {
        const failed = item.status === 'failed';
        const title = item.title || item.filename || '녹음';
        return (
          <div key={item.id} style={{ maxWidth: '100%', display: 'inline-flex', alignItems: 'center', gap: '7px', minHeight: 28, padding: '3px 9px', borderRadius: T.radius.pill, border: `1px solid ${failed ? '#fecaca' : '#bbf7d0'}`, background: failed ? '#fef2f2' : '#f0fdf4', color: failed ? '#991b1b' : '#047857', fontSize: T.font.xs, fontWeight: 800 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {failed ? '⚠' : '⤴'} "{title}" {failed ? '업로드 실패' : `${item.pct ?? 0}%`}
            </span>
            {failed && (
              <>
                <button onClick={() => retryPendingUpload?.(item.id)} style={{ ...smBtn(T.color.green), padding: '4px 8px', fontSize: T.font.xs }}>재시도</button>
                <button onClick={() => discardPendingUpload?.(item.id)} style={{ ...smBtn(T.color.slate), padding: '4px 8px', fontSize: T.font.xs }}>삭제</button>
              </>
            )}
          </div>
        );
      })}
    </div>
  ) : null;
  const internalIconSize = 22;
  const internalSidebarActionButton = {
    width: '48px',
    height: '48px',
    border: `1px solid ${T.color.circleBorder}`,
    borderRadius: '50%',
    background: T.color.surface,
    color: T.color.iconLine,
    cursor: disabledWork ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    opacity: disabledWork ? 0.55 : 1,
  };
  const internalActionWrap = { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '5px', minWidth: '62px' };
  const internalActionLabel = { color: T.color.muted, fontSize: T.font.xs, fontWeight: 700, lineHeight: 1.2, textAlign: 'center' };
  const setCircleHover = (e, on) => {
    if (e.currentTarget.disabled) return;
    e.currentTarget.style.background = on ? T.color.selectedBg : T.color.surface;
  };
  useEffect(() => {
    if (playIdx < 0 || editing) return;
    const el = document.getElementById('mseg-' + playIdx);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [playIdx, editing]);

  const diarizingTranscript = Array.isArray(active?.transcript) ? active.transcript : [];
  const diarizingStatus = STATUS[active?.status] || STATUS.diarizing;
  const diarizingDetail = (active?.status === 'diarizing' || active?.status === 'rediarize') ? (
    <div style={{ flex: 1, overflowY: 'auto', padding: narrow ? '14px' : '24px', background: T.color.page }}>
      <div style={{ maxWidth: '920px', margin: narrow ? 0 : '0 auto' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: T.color.page, paddingTop: '24px', marginBottom: '16px', boxShadow: '0 6px 8px -6px rgba(0,0,0,.12)' }}>
          <button onClick={() => { closeMeetingDetail(); setAdminView(false); }}
            style={{ margin: '0 0 12px', background: '#f1f1ef', color: '#32312e', border: '1px solid #e5e5e2', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>← 목록</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, flex: '1 1 260px', minWidth: 0, overflowWrap: 'anywhere' }}>{active.title}</h2>
            <button onClick={() => renameMeeting(active)} title="이름 수정" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#92918d', padding: '2px' }}>수정</button>
            <span style={{ fontSize: '12px', padding: '2px 10px', borderRadius: '12px', background: diarizingStatus.bg, color: diarizingStatus.color, fontWeight: 600 }}>{diarizingStatus.label}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* 이름은 상세 ⋮ 메뉴와 같게 맞춘다(클로바 묶음 C3b) — 동작은 그대로. */}
            {/* 중지(C3c) — 확인 창은 cancelStt 가 상태별로 묻는다(diarizing 묻고 · rediarize 안 묻는다). */}
            <button onClick={() => cancelStt(active)} style={{ fontSize: '12px', padding: '5px 10px', border: `1px solid ${T.color.outlineBtnBorder}`, borderRadius: T.radius.pill, background: T.color.surface, color: T.color.text, cursor: 'pointer', fontWeight: 700 }}>{active.status === 'rediarize' ? '화자분리 중지' : '변환 중지'}</button>
            <button onClick={() => setMoveOpen(true)} style={{ fontSize: '12px', padding: '5px 10px', border: `1px solid ${T.color.outlineBtnBorder}`, borderRadius: T.radius.pill, background: T.color.surface, color: T.color.text, cursor: 'pointer', fontWeight: 700 }}>노트 이동</button>
            <button onClick={() => delMeeting(active)} style={{ fontSize: '12px', padding: '5px 10px', border: `1px solid ${T.color.outlineBtnBorder}`, borderRadius: T.radius.pill, background: T.color.surface, color: T.color.danger, cursor: 'pointer', fontWeight: 700 }}>휴지통으로 이동</button>
          </div>
        </div>
        <div style={{ color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', fontWeight: 600, marginBottom: '16px' }}>
          화자 분석 중… {diarInfo(active, nowMs)} <span style={{ color: '#64635f', fontWeight: 400 }}>(전사는 먼저 표시됨 · 자동 새로고침)</span>
        </div>
        <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: narrow ? '14px' : '20px', border: '1px solid #e5e5e2' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#32312e', marginBottom: '12px' }}>변환 결과 (화자 분석 중 · {fmtDur(active.duration_sec)})</div>
          {diarizingTranscript.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {diarizingTranscript.map((s, i) => (
                <div key={i} id={`mseg-${i}`} data-testid={`mseg-${i}`} style={{ display: 'flex', flexWrap: narrow ? 'wrap' : 'nowrap', alignItems: 'flex-start', gap: '8px', fontSize: '14px', lineHeight: 1.5, padding: '6px 0' }}>
                  <span style={{ flexShrink: 0, minWidth: '54px', textAlign: 'right', color: '#92918d', fontVariantNumeric: 'tabular-nums' }}>{fmtDur(Math.floor(s.start || 0))}</span>
                  <span style={{ flex: '1 1 280px', minWidth: 0, color: '#32312e', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{s.text || ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '14px', fontFamily: 'inherit', color: '#32312e', overflowWrap: 'anywhere' }}>{active.transcript_text || '(변환 내용 없음)'}</pre>
          )}
        </div>
      </div>
    </div>
  ) : null;
  const detailOpen = !!active || adminView;
  // 상세가 열리면 «새 노트»도 닫는다(방어용 — 상세는 «새 노트» 화면과 같은 자리를 쓴다).
  useEffect(() => { if (detailOpen) setNewNoteOpen(false); }, [detailOpen]);
  // 🔴 내부 사이드바는 `!detailOpen` 블록 밖이라 상세가 열린 채로도 «새 노트»를 누를 수 있다 →
  //    상세를 먼저 닫지 않으면 새 노트 화면이 안 그려지고 «← 목록» 뒤에 뒤늦게 튀어나온다.
  const openNewNote = () => { closeMeetingDetail(); setAdminView(false); setNewNoteOpen(true); };
  // 폴더 패널 경계선 끌기 — 폭 = 시작 폭 + 이동량(손잡이 6px 중 어디를 잡아도 튀지 않게), [200, min(480, 행폭−420)].
  // 🔴 utils/colResize.startColResize 를 쓰지 않는다 — 그 함수는 mousedown 순간 전체화면 오버레이를 붙여
  //    mouseup 대상이 손잡이가 아니게 되고, 그러면 실제 브라우저에서 손잡이 dblclick(기본 폭 복귀)이 오지 않는다.
  //    그래서 오버레이는 3px 이상 움직여 진짜 끌기가 됐을 때만 붙인다(분할보기 iframe 이 mousemove 를 삼키지 않게).
  // 저장은 끌기를 마칠 때 한 번 — 클릭·더블클릭(움직임 없음)은 저장하지 않는다.
  // 웹 배율(<html> zoom)이 걸리면 clientX·rect 는 화면 px, 폭은 CSS px 다 → 이동량·행 폭을 행의 배율로 나눈다(jsdom·1.0 은 1).
  //   3px 끌기 판정은 화면 px 그대로 둔다(클릭·더블클릭 판정이 배율 따라 바뀌지 않게).
  const startFolderPanelDrag = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();   // 글자 선택 시작 방지
    if (folderDragCleanupRef.current) folderDragCleanupRef.current();
    const startX = e.clientX;
    const startW = folderPanelShownW;
    const row = panelRowRef.current;
    const rr = row?.getBoundingClientRect?.();
    const s = rr ? scaleOf(rr.width, row.offsetWidth) : 1;
    const rowW = rr?.width ? rr.width / s : panelRowW;
    const body = document.body;
    let moved = false;
    let ov = null;
    let last = startW;
    const onMove = (ev) => {
      const rawDx = ev.clientX - startX;
      if (!moved) {
        if (Math.abs(rawDx) < 3) return;
        moved = true;
        ov = document.createElement('div');
        ov.setAttribute('data-testid', 'meeting-folder-resize-overlay');
        ov.style.cssText = 'position:fixed;inset:0;z-index:99999;cursor:col-resize';
        body.appendChild(ov);
      }
      last = clampFolderPanelW(startW + rawDx / s, rowW);
      setFolderPanelW(last);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      body.style.userSelect = '';
      body.style.cursor = '';
      if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
      ov = null;
      if (folderDragCleanupRef.current === onUp) folderDragCleanupRef.current = null;
      if (moved) {
        moved = false;
        try { window.localStorage.setItem(FOLDER_PANEL_KEY, String(last)); } catch (err) { /* 저장 실패는 무시 */ }
      }
    };
    folderDragCleanupRef.current = onUp;   // 끄는 도중 언마운트·새 끌기 시작 = 그 자리에서 끌기를 마친다
    body.style.userSelect = 'none';
    body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  // 더블클릭 = 기본 폭(230) · 저장값 없음. 두 번째 mouseup 은 움직임이 없어 저장하지 않으므로 이 값이 마지막에 남는다.
  const resetFolderPanelW = () => {
    setFolderPanelW(FOLDER_PANEL_DEFAULT);
    try { window.localStorage.removeItem(FOLDER_PANEL_KEY); } catch (err) { /* 저장소 접근 불가 시 무시 */ }
  };
  // 폴더 행. depth 를 주면(폴더 트리 행) padding-left = 22 + (depth-1)*14px, toggle = {open,onToggle} | 'spacer'.
  // hoverActions = 폴더 관리 항목(하위 폴더 추가·이름 변경·폴더 이동) — 행에는 «⋯»(폴더 메뉴) 단추 하나만 둔다.
  // 그 단추는 평소 opacity 0, 마우스를 올리면 공유·삭제 단추 바로 왼쪽에 겹쳐 보인다(패널이 좁아도 이름 칸을 깎지 않게 ·
  // display 로 숨기지 않는다). 단추 3개를 겹쳐 띄우면 기본 폭 230px 에서 이름 칸 대부분을 덮어 이름 클릭을 가로챘다.
  // 누르면 항목 메뉴(position: fixed — 트리 스크롤 상자에 잘리지 않게)가 단추 아래(공간이 모자라면 위)에 뜬다.
  // fullName — 폴더 행 이름의 툴팁(전체 경로 'A > B > C'). 말줄임된 긴 이름을 끝까지 볼 수 있게 이름 칸에만 단다
  // (행에 달면 hover 단추 title 과 섞인다). 홈·전체 노트·미분류·휴지통은 넘기지 않는다.
  // addLabel — «폴더 추가» 라는 이름은 「전체 노트」 행에만 쓴다(e2e `button[aria-label='폴더 추가']`).
  const actionBtnStyle = { width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: T.color.iconLine, cursor: 'pointer', padding: 0 };
  // 폴더 메뉴 항목 높이는 고정 — 열 때 패널 높이(항목 수 × 32 + 여백 10)로 위·아래 자리를 고른다.
  const FOLDER_MENU_ITEM_H = 32;
  const folderMenuItemStyle = { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', height: `${FOLDER_MENU_ITEM_H}px`, boxSizing: 'border-box', padding: '0 9px', border: 'none', borderRadius: T.radius.sm, background: 'transparent', color: T.color.text, fontSize: T.font.sm, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', cursor: 'pointer' };
  // «⋯» — 같은 행이면 닫고, 아니면 단추 rect 기준으로 그 행 메뉴를 연다(오른쪽 = 단추 오른쪽 · 아래 4px, 모자라면 위).
  // 웹 배율(<html> zoom)이 걸리면 rect·innerHeight 는 화면 px, fixed top/right 는 CSS px 다 → 단추의 배율로 나눈다(jsdom·1.0 은 1).
  //   모바일(narrow)은 배율 1 로 고정한다 — 모바일 경로는 바꾸지 않는다.
  const toggleFolderMenu = (e, key, itemCount) => {
    e.stopPropagation();   // 행 onClick(폴더 선택)으로 올라가지 않게
    if (folderMenu?.key === key) { setFolderMenu(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const s = narrow ? 1 : scaleOf(r.width, e.currentTarget.offsetWidth);
    const panelH = itemCount * FOLDER_MENU_ITEM_H + 10;
    const pos = folderMenuPos({ rect: r, s, panelH, innerWidth: window.innerWidth, innerHeight: window.innerHeight,
      docClientWidth: document.documentElement?.clientWidth });
    setFolderMenu({ key, ...pos });
  };
  const renderFolderItem = ({ itemKey, Icon, label, fullName, sub, activeSel, indent, depth, toggle, count, onClick, onAdd, addLabel = '폴더 추가', onDel, onShare, hoverActions }) => {
    const pad = depth ? `${22 + (depth - 1) * 14}px` : (indent ? '22px' : '10px');
    const hover = Array.isArray(hoverActions) ? hoverActions.filter(Boolean) : [];
    const menuOpen = hover.length > 0 && !!folderMenu && folderMenu.key === itemKey;
    return (
      <div key={itemKey} className="gw-mrow" onClick={onClick} data-depth={depth || undefined}
        onMouseEnter={(e) => { if (!activeSel) e.currentTarget.style.background = T.color.selectedBg; }}
        onMouseLeave={(e) => { if (!activeSel) e.currentTarget.style.background = 'transparent'; }}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px', padding: `8px 10px 8px ${pad}`, borderRadius: T.radius.md, cursor: 'pointer', background: activeSel ? T.color.selectedBg : 'transparent', color: activeSel ? T.color.selectedText : T.color.text }}>
        {toggle === 'spacer' && <span aria-hidden="true" style={{ width: '14px', flexShrink: 0, marginRight: '-4px' }} />}
        {toggle && toggle !== 'spacer' && (
          <button type="button" aria-label={toggle.open ? '접기' : '펼치기'} title={toggle.open ? '접기' : '펼치기'} aria-expanded={!!toggle.open}
            onClick={(e) => { e.stopPropagation(); toggle.onToggle(); }}
            style={{ ...actionBtnStyle, width: '14px', height: '20px', marginRight: '-4px', flexShrink: 0, color: T.color.faint }}>
            {toggle.open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </button>
        )}
        <Icon size={16} stroke={1.9} color={T.color.iconLine} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div title={fullName || undefined} style={{ fontSize: T.font.base, color: activeSel ? T.color.selectedText : T.color.text, fontWeight: activeSel ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
          {sub && <div style={{ fontSize: T.font.xs, color: T.color.faint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
        </div>
        {count !== null && count !== undefined && (
          <span style={{ flexShrink: 0, fontSize: T.font.xs, fontWeight: 700, color: T.color.faint, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
        )}
        {onAdd && (
          /* 평소엔 opacity 0(마우스·초점 때만 보임) — display/visibility 로 숨기면 키보드·시험이 못 누른다 */
          <button onClick={(e) => { e.stopPropagation(); onAdd(); }} title={addLabel} aria-label={addLabel}
            className={narrow ? undefined : 'gw-mrow-add'}
            style={{ ...actionBtnStyle, fontSize: '17px', fontWeight: 800, lineHeight: 1, opacity: narrow ? 1 : undefined }}>
            +
          </button>
        )}
        {(hover.length > 0 || onShare || onDel) && (
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
            {hover.length > 0 && (
              <span className={narrow ? undefined : 'gw-mrow-acts'} data-testid="folder-row-hover-actions"
                style={{ position: 'absolute', right: '100%', top: '50%', transform: 'translateY(-50%)', zIndex: 1, display: 'inline-flex', alignItems: 'center', background: T.color.selectedBg, borderRadius: T.radius.md, ...(menuOpen ? { opacity: 1, pointerEvents: 'auto' } : null) }}>
                {/* 메뉴가 열린 동안은 마우스가 떠나도 보이게 인라인으로 덮는다(클래스 규칙보다 우선). 🔴 이름에 «폴더 추가» 금지 */}
                <button ref={menuOpen ? folderMenuBtnRef : undefined} type="button" title="폴더 메뉴" aria-label="폴더 메뉴"
                  aria-haspopup="menu" aria-expanded={menuOpen}
                  onClick={(e) => toggleFolderMenu(e, itemKey, hover.length)} style={actionBtnStyle}>
                  <IconDots size={15} />
                </button>
              </span>
            )}
            {/* 🔴 패널은 위 hover span 밖(이 바깥 span 의 자식)에 둔다 — hover span 은 transform 이 있어 그 안에 두면
                position: fixed 의 기준이 화면이 아니라 그 span 이 되고, 트리 스크롤 상자에 잘린다.
                행(.gw-mrow) 안이라 클릭이 행 onClick 으로 올라가므로 패널·항목 모두 stopPropagation. */}
            {menuOpen && (
              <span ref={folderMenuPanelRef} role="menu" aria-label="폴더 메뉴" onClick={(e) => e.stopPropagation()}
                style={{ position: 'fixed', top: `${folderMenu.top}px`, right: `${folderMenu.right}px`, zIndex: 30, minWidth: '150px', boxSizing: 'border-box', padding: '4px', display: 'flex', flexDirection: 'column', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.surface, boxShadow: T.shadow.raised, cursor: 'default' }}>
                {hover.map((a) => (
                  <button key={a.label} type="button" role="menuitem"
                    onClick={(e) => { e.stopPropagation(); setFolderMenu(null); a.onClick(); }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = T.color.selectedBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    style={folderMenuItemStyle}>
                    <a.Icon size={15} />
                    <span>{a.label}</span>
                  </button>
                ))}
              </span>
            )}
            {onShare && (
              <button onClick={(e) => { e.stopPropagation(); onShare(); }} title="공유" aria-label="공유" style={actionBtnStyle}>
                <IconShare size={15} />
              </button>
            )}
            {onDel && (
              <button onClick={(e) => { e.stopPropagation(); onDel(); }} title="삭제" aria-label="삭제"
                style={{ ...actionBtnStyle, color: T.color.faint }}>
                <IconX size={15} />
              </button>
            )}
          </span>
        )}
      </div>
    );
  };
  // 개수는 응답이 이상하거나 실패하면 숫자만 숨긴다(화면은 그대로).
  const countOf = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const allCount = countOf(stats?.all);
  const unfiledCount = countOf(stats?.unfiled);
  const folderNoteCount = (fid) => (allCount === null ? null : (countOf(stats?.folders?.[fid]) ?? 0));
  // 🔴 메뉴 행들은 형제 div 로 평평하게 둔다(들여쓰기는 padding 으로만) — 「전체 노트」 행이 폴더 행을
  //    감싸면 e2e 가 폴더 행을 잘못 집어 다른 폴더의 삭제·공유 단추를 누른다.
  // 🔴 minHeight:0 이 없으면 폴더 행 전체 높이가 최소높이로 굳어 스크롤이 죽고 「휴지통」 행이 잘린다.
  //    className 'sb-scroll' 은 그대로 둔다(e2e 가 aside.sb-scroll > div.sb-scroll 로 폴더 행을 집는다).
  const folderInputStyle = { width: '100%', boxSizing: 'border-box', height: '30px', padding: '0 8px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.surface, color: T.color.text, fontSize: T.font.base, outline: 'none' };
  // 1단계 «새 폴더» 입력칸 — 데스크톱 트리와 모바일 서랍이 같은 요소를 쓴다.
  const newFolderInput = newFolderOpen ? (
    <div style={{ padding: '2px 0 4px 22px' }}>
      <input autoFocus value={newFolderName} placeholder="새 폴더" maxLength={100}
        onChange={(e) => setNewFolderName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commitNewFolder(e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); closeNewFolder(); }
        }}
        onBlur={(e) => commitNewFolder(e.target.value)}
        style={folderInputStyle} />
    </div>
  ) : null;
  // «새 하위 폴더» 입력칸 — 부모 폴더 행 바로 아래(들여쓰기 = 한 단계 아래 행과 같게).
  const childFolderInput = (depth) => (
    <div key={`child-input-${childFolderParent}`} style={{ padding: `2px 0 4px ${22 + (Math.max(1, depth) - 1) * 14}px` }}>
      <input autoFocus value={childFolderName} placeholder="새 하위 폴더" maxLength={100}
        onChange={(e) => setChildFolderName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commitChildFolder(e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); closeChildFolder(); }
        }}
        onBlur={(e) => commitChildFolder(e.target.value)}
        style={folderInputStyle} />
    </div>
  );
  const shareSub = (f) => (f.role !== 'owner' ? `${f.owner_name || '?'} 공유 · ${f.role === 'edit' ? '편집' : '읽기'}` : null);
  const openTrashView = () => { setHomeView(false); setTrashView(true); setAdminView(false); setSel(null); setAllView(true); closeMeetingDetail(); setNewNoteOpen(false); setFolderDrawerOpen(false); };
  const foldersStatus = (
    <>
      {foldersLoading && folders.length === 0 && <div style={{ padding: '8px 10px', fontSize: '12px', color: '#92918d' }}>폴더 로딩 중…</div>}
      {foldersError && <button onClick={loadFolders} style={{ width: '100%', margin: '4px 0', padding: '7px 8px', border: '1px solid #fecaca', borderRadius: '8px', background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontSize: '12px', fontWeight: 600, textAlign: 'left' }}>{foldersError} 다시 시도</button>}
    </>
  );
  // 데스크톱 트리 — 트리 순서(DFS)로 펼친 폴더만. 접힌 폴더의 하위는 그리지 않는다.
  const visibleFolders = flattenTree(folders, (key) => expandedKeys.has(key));
  // 폴더 이름 툴팁 = 전체 경로('A > B > C' — utils pathText 와 같은 형식). 트리 순서(DFS)라 부모가 늘 먼저 온다 →
  // 한 번 돌며 만든다(행마다 pathText 를 부르면 트리를 폴더 수만큼 다시 짓고, 녹음 중엔 1초마다 다시 그린다).
  const folderPathByKey = new Map();
  visibleFolders.forEach((it) => {
    const name = String(it.folder?.name ?? '');
    const parentPath = it.parentKey != null ? folderPathByKey.get(it.parentKey) : undefined;
    folderPathByKey.set(it.key, parentPath !== undefined ? `${parentPath} > ${name}` : name);
  });
  const folderTree = (
    <div className="sb-scroll" data-testid="meeting-folder-tree" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '6px 10px' }}>
      {/* «홈»은 메뉴 맨 위(클로바 묶음 F2b) — «전체 노트»와 동시에 선택 표시되지 않는다(!homeView). */}
      {renderFolderItem({
        itemKey: 'home',
        Icon: IconHome,
        label: '홈',
        activeSel: homeView && !trashView && !adminView,
        onClick: selectHome,
      })}
      {renderFolderItem({
        Icon: IconFileText,
        label: '전체 노트',
        activeSel: allView && !sel && !trashView && !adminView && !homeView,
        count: allCount,
        onClick: selectAllNotes,
        onAdd: openNewFolder,
      })}
      {/* 🔴 서랍이 열린 동안(데스크톱 «폴더별 보기»)은 트리에 입력칸을 그리지 않는다 — 같은 입력칸(autoFocus·onBlur=저장)이
          트리와 서랍에 둘 다 있으면 두 번째가 포커스를 가져가 첫 칸이 빈 이름으로 닫힌다. */}
      {!folderDrawerOpen && newFolderInput}
      {renderFolderItem({ Icon: IconFolderOpen, label: '미분류', activeSel: !sel && !allView && !trashView && !adminView, indent: true, count: unfiledCount, onClick: () => selectFolder(null) })}
      {foldersStatus}
      {visibleFolders.map((it) => {
        const f = it.folder;
        const caps = folderCaps(f, it.depth);
        return (
          <Fragment key={it.key}>
            {renderFolderItem({
              itemKey: `row-${it.key}`,
              Icon: IconFolder,
              label: f.name,
              fullName: folderPathByKey.get(it.key),
              sub: shareSub(f),
              activeSel: sel?.id === f.id && !trashView && !adminView,
              depth: it.depth,
              toggle: it.hasChildren ? { open: expandedKeys.has(it.key), onToggle: () => toggleExpanded(it.key) } : 'spacer',
              count: folderNoteCount(f.id),
              onClick: () => selectFolder(f),
              hoverActions: [
                caps.canCreateChild ? { label: '하위 폴더 추가', Icon: IconFolderPlus, onClick: () => openChildFolder(f) } : null,
                caps.canManage ? { label: '이름 변경', Icon: IconPencil, onClick: () => renameFolder(f) } : null,
                caps.canManage ? { label: '폴더 이동', Icon: IconArrowsMove, onClick: () => setFolderMove(f) } : null,
              ],
              onShare: caps.canShare ? () => openShare(f) : null,
              onDel: caps.canManage ? () => delFolder(f) : null,
            })}
            {!folderDrawerOpen && childFolderParent != null && String(childFolderParent) === it.key && childFolderInput(it.depth + 1)}
          </Fragment>
        );
      })}
      <div style={{ borderTop: '1px solid #e5e5e2', margin: '6px 0' }} />
      {renderFolderItem({ Icon: IconTrash, label: '휴지통', activeSel: trashView, onClick: openTrashView })}
    </div>
  );

  // 페이지 머리 «AI 노트»(시안 F g1-024) — 상세(자기 «← 목록» 머리)·녹음 전체 화면에서는 그리지 않는다.
  const showPageHead = !detailOpen && !(recordingScreenOpen && !recordingScreenMinimized);

  return (
    <AppShell app="meetings" ownTabbarPad style={{ fontFamily: 'var(--gw-font)', background: T.color.page }}>
      <style>{`.sb-scroll{scrollbar-width:thin;scrollbar-color:rgba(255,255,255,.2) transparent}.sb-scroll::-webkit-scrollbar{width:6px}.sb-scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,.15);border-radius:3px}.gw-mrow-add{opacity:0;transition:opacity .15s}.gw-mrow:hover .gw-mrow-add,.gw-mrow:focus-within .gw-mrow-add,.gw-mrow-add:focus{opacity:1}.gw-mrow-acts{opacity:0;pointer-events:none;transition:opacity .15s}.gw-mrow:hover .gw-mrow-acts,.gw-mrow:focus-within .gw-mrow-acts{opacity:1;pointer-events:auto}@media (max-height:500px){.gw-mpagehead{display:none}}`}</style>
      {/* ── 사이드바(AI 채팅과 동일 톤) ── */}
      {/* 열 체인의 모든 칸에 minHeight:0 — 공지 배너가 있어도 아래 영역이 창을 넘지 않게(ChatPage 관례) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        <Announcements scope="meetings" />
        {/* 페이지 머리 — 행의 형제(행→열 래퍼 minHeight 사슬은 그대로). h1·원 크기는 공용 PageHeader(기본 lg)와
            index.html 폭별 규칙이 정한다 — 여기서 덮지 않는다. actions([녹음][새 노트])는 목록·홈 머리와 겹쳐 두지 않는다. */}
        {showPageHead && (
          <div data-gw-pagehead="" className="gw-mpagehead" style={{ flexShrink: 0, padding: narrow ? '20px 16px 0' : '30px 40px 0', background: T.color.page }}>
            <PageHeader icon={IconMicrophone} tone="blue" title="AI 노트" subtitle="회의의 모든 맥락을, 다음 업무로 연결합니다." />
          </div>
        )}
        <div ref={setPanelRowNode} style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
        {recordingScreenOpen && !recordingScreenMinimized ? (
          <RecordingView
            mobile={narrow}
            sel={sel}
            onMinimize={minimizeRecordingScreen}
          />
        ) : (
        <>
        {!narrow && (
          <aside className="sb-scroll" style={{ width: `${folderPanelShownW}px`, flex: `0 0 ${folderPanelShownW}px`, position: 'relative', background: '#fff', borderRight: '1px solid #e5e5e2', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '12px 10px 10px', borderBottom: '1px solid #e5e5e2', flexShrink: 0 }}>
              {/* 진입점은 «녹음하기 | 새 노트» 두 단추뿐이다(클로바 묶음 B4c-2) — 업로드는 «새 노트»
                  화면의 파일 첨부·끌어다 놓기로만 간다. 🔴 aria-label «녹음»·«새 노트» 는 그대로 둔다
                  (e2e `get_by_label("녹음")`·jest `name: '새 노트'` 가 이 이름을 쓴다). */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '18px' }}>
                <div style={internalActionWrap}>
                  <button onClick={startRec} disabled={disabledWork} title="녹음하기" aria-label="녹음" onMouseEnter={(e) => setCircleHover(e, true)} onMouseLeave={(e) => setCircleHover(e, false)} style={internalSidebarActionButton}><IconMicrophone size={internalIconSize} /></button>
                  <span style={internalActionLabel}>녹음하기</span>
                </div>
                <div style={internalActionWrap}>
                  <button onClick={openNewNote} disabled={disabledWork} title="새 노트" aria-label="새 노트" onMouseEnter={(e) => setCircleHover(e, true)} onMouseLeave={(e) => setCircleHover(e, false)} style={internalSidebarActionButton}><IconPlus size={internalIconSize} /></button>
                  <span style={internalActionLabel}>새 노트</span>
                </div>
              </div>
              {uploadPct != null && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.font.xs, color: T.color.muted, marginBottom: '4px' }}>
                    <span>업로드 중…</span><span>{uploadPct}%</span>
                  </div>
                  <div style={{ height: '5px', background: T.color.borderSoft, borderRadius: T.radius.pill, overflow: 'hidden' }}>
                    <div style={{ width: `${uploadPct}%`, height: '100%', background: T.color.green, transition: 'width .2s' }} />
                  </div>
                </div>
              )}
            </div>
            {folderTree}
            {/* 폭 조절 손잡이 — aside 의 마지막 직계 자식(트리 밖: `aside.sb-scroll > div.sb-scroll` 자리를 지킨다).
                🔴 right:-6px = 테두리부터 목록 쪽 6px 를 덮는다. right:0 이면 폴더 트리 세로 스크롤바(6px)를 가려
                스크롤바 끌기가 막힌다. 🔴 className sb-scroll·cursor:pointer 금지(시험·e2e 폴더 행 셀렉터).
                zIndex 10 = 목록 쪽 sticky(5) 위 · 드롭다운(20/30)·Modal(50) 아래. */}
            <div role="separator" aria-orientation="vertical" aria-label="폴더 패널 너비 조절" data-testid="meeting-folder-resizer"
              aria-valuenow={folderPanelShownW} aria-valuemin={FOLDER_PANEL_MIN} aria-valuemax={folderPanelMaxFor(panelRowW)}
              title="드래그하여 너비 조절 · 더블클릭하면 기본 너비"
              onMouseDown={startFolderPanelDrag} onDoubleClick={resetFolderPanelW}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(100,116,139,.28)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              style={{ position: 'absolute', top: 0, bottom: 0, right: '-6px', width: '6px', cursor: 'col-resize', zIndex: 10, background: 'transparent' }} />
          </aside>
        )}
        {/* 폴더 컬럼 — 모바일=off-canvas 드로어, 데스크톱은 내부 사이드바에 표시.
            데스크톱도 목록의 «폴더별 보기» 탭으로 같은 서랍을 연다(열린 동안만 그린다 · 바깥 막을 눌러 닫는다). */}
        {folderDrawerOpen && (
          <div onClick={() => setFolderDrawerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1101, background: 'rgba(0,0,0,.4)' }} />
        )}
        {(narrow || folderDrawerOpen) && (
        <aside style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '260px', maxWidth: '82vw', zIndex: 1102, background: '#fff', borderRight: '1px solid #e5e5e2', display: 'flex', flexDirection: 'column', transform: folderDrawerOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .25s ease', boxShadow: folderDrawerOpen ? '2px 0 16px rgba(0,0,0,.18)' : 'none' }}>
          {/* 모바일 서랍 — 한 단계씩 들어가는 폴더 목록(관리 단추 규칙은 데스크톱과 같은 folderCaps).
              🔴 공유·위치 옮기기는 Modal(z-index 50)이라 서랍(z-index 1102) 뒤에 가린다 → 서랍을 닫고 연다. */}
          <MobileFolderDrawer
            folders={folders} sel={sel}
            homeView={homeView} allView={allView} trashView={trashView} adminView={adminView}
            allCount={allCount} unfiledCount={unfiledCount} folderCount={folderNoteCount}
            onHome={selectHome} onAll={selectAllNotes} onUnfiled={() => selectFolder(null)} onTrash={openTrashView}
            onSelectFolder={selectFolder}
            onAddRoot={openNewFolder} newFolderSlot={<>{newFolderInput}{foldersStatus}</>}
            childInputParentId={childFolderParent}
            childInputSlot={childFolderParent != null ? childFolderInput(1) : null}
            actions={{
              addChild: openChildFolder,
              share: (f) => { setFolderDrawerOpen(false); openShare(f); },
              rename: renameFolder,
              move: (f) => { setFolderDrawerOpen(false); setFolderMove(f); },
              remove: delFolder,
            }}
          />
        </aside>
        )}
        {/* 회의록 목록 — meetings/MeetingList
            상세를 열면 화면 폭 무관하게 목록을 접는다(드릴다운). 데스크톱에서 목록과 상세를
            나란히 두면 상세가 절반으로 눌려 요약·전사가 읽기 어렵다(마스터 지시 2026-08-12).
            복귀는 상세의 '← 목록' 버튼 → closeMeetingDetail(). */}
        {!detailOpen && (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, background: T.color.page, display: 'flex', flexDirection: 'column' }}>
          {newNoteOpen ? (
          <NewNoteView
            mobile={narrow}
            folders={folders}
            sel={sel}
            diarizeDefault={diarizeUpload}
            disabled={disabledWork}
            uploadPct={uploadPct}
            onStartRecording={(meta) => { setDiarizeUpload(!!meta?.diarize); startRecording(meta); }}
            onUploadFile={async (file, meta) => {
              setDiarizeUpload(!!meta?.diarize);
              const ok = await uploadBlob(
                file,
                file.name || `audio_${Date.now()}`,
                (meta?.title || '').trim() || file.name || ('회의 ' + new Date().toLocaleString('ko-KR')),
                undefined,
                'upload',
                meta,
              );
              if (ok) setNewNoteOpen(false);
            }}
            onClose={() => setNewNoteOpen(false)}
          />
          ) : homeView ? (
          /* «홈» — 보일 때만 마운트되므로 상세·새 노트·다른 보기에서 돌아올 때마다 다시 읽는다.
             🔴 녹음은 홈이 다시 마운트된 **뒤에** 업로드가 끝난다 → reloadKey(uploadVersion)로 한 번 더 읽힌다.
             홈 검색어는 homeQueryRef 가 들고 있다가 다시 마운트될 때 initialQuery 로 돌려준다(H2). */
          <>
          {recordingUploadQueue}
          <MeetingHome
            mobile={narrow}
            openMeeting={openMeeting}
            onOpenFolders={() => setFolderDrawerOpen(true)}
            onNewNote={openNewNote}
            startRec={startRec}
            disabledWork={disabledWork}
            reloadKey={uploadVersion}
            initialQuery={homeQueryRef.current}
            onQueryChange={(v) => { homeQueryRef.current = v; }}
          />
          </>
          ) : (
          <>
          {recordingUploadQueue}
          <MeetingList
            mobile={narrow} trashView={trashView} sel={sel} allView={allView} folders={folders}
            sort={sort} order={order} onSortChange={changeSort} fpEdit={fpEdit}
            setFpEdit={setFpEdit} openFolderPrompt={openFolderPrompt} fpText={fpText} setFpText={setFpText} saveFolderPrompt={saveFolderPrompt}
            uploadPct={uploadPct} recordingBusy={recordingBusy} startRec={startRec} onNewNote={openNewNote}
            setFolderDrawerOpen={setFolderDrawerOpen} trash={trash} restoreMeeting={restoreMeeting} purgeMeeting={purgeMeeting}
            folderTrash={folderTrash} restoreFolder={restoreFolder}
            meetings={meetings} openMeeting={openMeeting} active={active} queueText={queueText} adminView={adminView} cancelStt={cancelStt}
            meetingsLoading={meetingsLoading} meetingsError={meetingsError} trashLoading={trashLoading} trashError={trashError}
            onBulkMove={bulkMoveMeetings} onBulkTrash={bulkTrashMeetings}
            onBulkRestore={bulkRestoreMeetings} onBulkPurge={bulkPurgeMeetings}
            query={listQuery} onQueryChange={setListQuery} resultQuery={meetingsQuery}
          />
          </>
          )}
        </div>
        )}

        {/* 상세 — meetings/MeetingDetail */}
        {detailOpen && (diarizingDetail || (
          <MeetingDetail
            active={active} adminView={adminView} setActive={setActiveFromDetail} setAdminView={setAdminView} mobile={narrow} usage={usage}
            auditLog={auditLog} renameMeeting={renameMeeting} startStt={startStt} cancelStt={cancelStt} sttStarting={sttStarting} dlAudio={dlAudio} dlTxt={dlTxt} dlSummary={dlSummary} dlSummaryNotes={dlSummaryNotes}
            setMoveOpen={setMoveOpen} delMeeting={delMeeting} audioUrl={audioUrl} audioError={audioError} retryAudio={retryAudio} audioRef={audioRef} setPlaySec={setPlaySec} nowMs={nowMs}
            queueText={queueText} sumMode={sumMode} setSumMode={setSumModeFromUser} genSummary={genSummary} summarizing={summarizingId === active?.id} customPrompt={customPrompt}
            setCustomPrompt={setCustomPrompt} editing={editing} startEdit={startEdit} saveEdit={saveEdit} saving={saving} setEditing={setEditing}
            seekTo={seekTo} playSec={playSec} spkDisp={spkDisp} spkMap={spkMap} setSpkMap={setSpkMap} editSegs={editSegs}
            setEditSegs={setEditSegs} markSaving={markSaving}
            toggleSegFlag={toggleSegFlag} editSegMemo={editSegMemo}
            saveRecordingNotes={saveRecordingNotes} notesSaving={notesSaving}
            setRagOpt={setRagOpt}
            requestRediarize={requestRediarize} rediarizing={rediarizing}
            folders={folders}
          />
        ))}

      {/* 오버레이 모달 3종 */}
      {longAsk && active && (() => {
        const len = (active.transcript_text || '').length;
        const man = (len / 10000).toFixed(1);
        const approxTok = Math.round(len / 2);
        return (
          <Modal title="긴 전사 - 요약 범위 선택" onClose={() => setLongAsk(false)}>
            <div style={{ fontSize: T.font.base, color: T.color.text, lineHeight: 1.65 }}>
              전사가 깁니다 (<b>약 {man}만 자</b>).<br />
              <b>전체 요약</b>은 전사 전부를 사용해 더 정확하지만, 토큰을 많이 사용하고(대략 {approxTok.toLocaleString()}+ 토큰)
              <b> 이번 달 사용 한도가 더 차감</b>됩니다. 매우 길면 자동으로 나눠 요약합니다.<br />
              <b>앞부분만</b>은 처음 약 6만 자만 요약합니다(절약).
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
              <button onClick={() => genSummary('full')} style={smBtn(T.color.green)}>전체 요약</button>
              <button onClick={() => genSummary('head')} style={smBtn(T.color.slate)}>앞부분만 (절약)</button>
              <button onClick={() => setLongAsk(false)} style={smBtn(T.color.slate)}>취소</button>
            </div>
          </Modal>
        );
      })()}

      {shareFolder && (
        <Modal title={`${shareFolder.name} · 공유`} onClose={() => setShareFolder(null)}>
          <input value={userQuery} onChange={(e) => doSearchUsers(e.target.value)} placeholder="이름·이메일로 사용자 검색"
            style={{ width: '100%', padding: '8px', borderRadius: T.radius.md, border: `1px solid ${T.color.border}`, boxSizing: 'border-box', fontSize: T.font.base }} />
          {userResults.length > 0 && (
            <div style={{ border: `1px solid ${T.color.borderSoft}`, borderRadius: T.radius.md, marginTop: '4px', maxHeight: '170px', overflowY: 'auto' }}>
              {userResults.map((u) => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid #f1f1ef' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: T.font.base, fontWeight: 600, ...softWrap }}>{u.name}</div>
                    <div style={{ fontSize: T.font.xs, color: T.color.faint, ...softWrap }}>{u.email}</div>
                  </div>
                  <button onClick={() => addShare(u, 'view')} style={smBtn(T.color.slate)}>읽기</button>
                  <button onClick={() => addShare(u, 'edit')} style={smBtn(T.color.green)}>편집</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: T.font.sm, fontWeight: 700, color: T.color.iconLine, margin: '14px 0 6px' }}>공유된 사용자</div>
          {shares.length === 0 ? <div style={{ fontSize: T.font.base, color: T.color.faint }}>아직 공유한 사용자가 없습니다.</div>
            : shares.map((s) => (
              <div key={s.user_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', borderBottom: '1px solid #f1f1ef' }}>
                <div style={{ flex: 1, minWidth: 0, ...softWrap }}>
                  <span style={{ fontSize: T.font.base, fontWeight: 600 }}>{s.name}</span>
                  <span style={{ fontSize: T.font.xs, color: T.color.faint, marginLeft: '6px' }}>{s.email}</span>
                </div>
                <span style={{ fontSize: '11px', padding: '1px 8px', borderRadius: T.radius.pill, border: s.role === 'edit' ? '1px solid #e5e5e2' : '1px solid transparent', background: s.role === 'edit' ? '#fff' : '#f1f1ef', color: s.role === 'edit' ? '#2383e2' : '#64635f', fontWeight: 600 }}>{s.role === 'edit' ? '편집' : '읽기'}</span>
                <button onClick={() => removeShare(s.user_id)} title="해제" aria-label="해제" style={{ background: 'none', border: 'none', color: '#92918d', cursor: 'pointer', fontSize: '14px' }}>×</button>
              </div>
            ))}
        </Modal>
      )}

      {/* 이동 모달 — 단건(상세의 '이동')과 여러 개(목록의 '노트 이동')가 같이 쓴다.
          🔴 묶음 모드에서는 active 가 없을 수 있으므로 active. 를 직접 읽지 않고, 현재 위치 강조도 하지 않는다. */}
      {((moveOpen && active) || bulkMoveIds) && (
        <Modal title="폴더 이동" onClose={closeMoveModal}>
          {bulkMoveIds && (
            <div style={{ fontSize: T.font.sm, color: T.color.muted, marginBottom: '8px' }}>선택한 {bulkMoveIds.length}개 노트를 옮깁니다.</div>
          )}
          {/* 미분류 + 들여쓴 트리. 지금 폴더(단건)·읽기 권한 폴더는 고를 수 없다(묶음은 지금 폴더 표시 없음). */}
          <FolderTreePicker
            folders={folders}
            mode="note"
            currentId={bulkMoveIds ? undefined : (active?.folder_id ?? null)}
            onPick={(fid) => (bulkMoveIds ? doBulkMove(fid) : doMove(fid))}
          />
        </Modal>
      )}

      {/* 폴더 위치 옮기기 — 같은 소유자의 트리 안에서만(«최상위»는 소유자만). 판정은 서버가 한다. */}
      {folderMove && (
        <Modal title={`${folderMove.name} · 위치 옮기기`} onClose={() => setFolderMove(null)}>
          <FolderTreePicker
            folders={folders}
            mode="folder"
            movingFolder={folderMove}
            canMoveToTop={folderMove.role === 'owner'}
            onPick={doFolderMove}
          />
        </Modal>
      )}
        </>
        )}
        </div>
      </div>
    </AppShell>
  );
}

// Modal 컴포넌트는 pages/meetings/ 로, 스타일·화자 헬퍼는 utils/meetingUtil 로 분리(상단 import).
